import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Button,
  IconButton,
  Alert,
  OutlinedInput
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../Configuration";
import { useFirebaseData } from "../contexts/FirebaseDataContext";
import { isAdmin, isPresident } from "../utils/permissions";
import {
  buildInstructorByIdMap,
  buildCourseNameToInstructorMap,
  getDisplayInstructorName,
  getInstructorNameForCourseName
} from '../utils/instructorDisplay';
import { useNavigate } from "react-router-dom";
import { isUrduText, getUrduSxProps } from '../utils/urduFont';
import {
  dedupeAttendanceBuckets,
  getCampusActiveStudentCount,
  sessionPresentPct,
  ATTENDANCE_PCT_GOOD_ABOVE,
  ATTENDANCE_PCT_WARN_MIN
} from '../utils/attendanceMath';
import * as XLSX from 'xlsx-js-style';

dayjs.extend(isoWeek);

// Slot definitions (same as CourseSelector)
const SLOTS = {
    'monday': 4,
    'tuesday': 4,
    'wednesday': 4,
    'thursday': 4,
    'friday': 3
};

// When campusCode matches no batch token below.
const REPORT_WEEK_ANCHOR_DEFAULT = '2026-04-14';

/**
 * Batch snippet in campusCode → week-1 anchor (ISO week containing this date).
 * Only campusCode is searched (case-insensitive). First rule wins.
 * Last year: …2025-26… — new batch: …2026_27…
 */
const REPORT_BATCH_WEEK_ANCHOR_RULES = [
  {
    tokens: ['2026_27', '2026-27'],
    anchor: '2026-04-06'
  },
  {
    tokens: ['2025-26', '2025_26'],
    anchor: '2025-04-14'
  }
];

function getReportWeekStartAnchorForCampus(campus) {
  if (!campus) return REPORT_WEEK_ANCHOR_DEFAULT;
  const code = String(campus.campusCode ?? '').toLowerCase();
  if (!code) return REPORT_WEEK_ANCHOR_DEFAULT;

  for (const rule of REPORT_BATCH_WEEK_ANCHOR_RULES) {
    for (const token of rule.tokens) {
      if (code.includes(token.toLowerCase())) {
        return rule.anchor;
      }
    }
  }

  return REPORT_WEEK_ANCHOR_DEFAULT;
}

function parseAttendanceSessionStart(att) {
  return dayjs(
    att.startTime?.replace(/-(\d{1,2}):(\d{2})$/, ' $1:$2'),
    'YYYY-MM-DD HH:mm'
  );
}

/**
 * Session count for a course on a campus from the batch Week-1 Monday (ISO week of
 * campus report anchor) through the last calendar day of the selected week (inclusive).
 * Avoids all-time totals that confuse the weekly Subject Summary.
 */
function countConductedThroughSelectedWeek(
  courseName,
  campusCode,
  attendanceList,
  batchAnchorDate,
  selectedWeekEnd
) {
  if (!campusCode) return 0;
  const batchStart = dayjs(batchAnchorDate).startOf('isoWeek').startOf('day');
  const weekLastDay = dayjs(selectedWeekEnd).startOf('day');
  let n = 0;
  for (const a of attendanceList || []) {
    if (a.campusCode !== campusCode) continue;
    if ((a.courseName || 'Unknown') !== courseName) continue;
    const t = parseAttendanceSessionStart(a);
    if (!t.isValid()) continue;
    const tDay = t.startOf('day');
    if (tDay.isBefore(batchStart)) continue;
    if (tDay.isAfter(weekLastDay)) continue;
    n++;
  }
  return n;
}

/**
 * Normalize subject title so e.g. "Arabic Grammer" and "Arabic Grammar" club together.
 * Uses global grammer→grammar (not only \\b) so Unicode adjacency / odd spacing still matches.
 */
function normalizeCourseSubjectKey(courseName) {
  let s = String(courseName ?? '').trim();
  s = s.replace(/[\u200B-\u200D\uFEFF]/g, '');
  try {
    s = s.normalize('NFKC');
  } catch {
    /* ignore */
  }
  s = s
    .toLowerCase()
    .replace(/\s+/g, ' ');
  s = s.replace(/grammer/g, 'grammar');
  s = s.replace(/gramar/g, 'grammar');
  s = s.replace(/([a-z])grammar\b/g, '$1 grammar');
  return s;
}

function canonicalCourseDisplayName(originalNames) {
  const uniq = [
    ...new Set(
      originalNames.map((n) => String(n || '').trim()).filter(Boolean)
    )
  ];
  if (uniq.length === 0) return '';
  const preferred = uniq.find(
    (n) => /\bgrammar\b/i.test(n) && !/\bgrammer\b/i.test(n)
  );
  let picked = preferred || [...uniq].sort((a, b) => a.localeCompare(b))[0];
  picked = picked.replace(/\bGrammer\b/g, 'Grammar').replace(/\bgrammer\b/g, 'grammar');
  return picked;
}

/** Like countConductedThroughSelectedWeek but matches attendance courseName after normalizeCourseSubjectKey. */
function countConductedForSubjectKey(
  subjectKey,
  campusCode,
  attendanceList,
  batchAnchorDate,
  selectedWeekEnd
) {
  if (!campusCode || !subjectKey) return 0;
  const batchStart = dayjs(batchAnchorDate).startOf('isoWeek').startOf('day');
  const weekLastDay = dayjs(selectedWeekEnd).startOf('day');
  let n = 0;
  for (const a of attendanceList || []) {
    if (a.campusCode !== campusCode) continue;
    if (normalizeCourseSubjectKey(a.courseName || '') !== subjectKey) continue;
    const t = parseAttendanceSessionStart(a);
    if (!t.isValid()) continue;
    const tDay = t.startOf('day');
    if (tDay.isBefore(batchStart)) continue;
    if (tDay.isAfter(weekLastDay)) continue;
    n++;
  }
  return n;
}

function attendanceSessionDayInRange(t, weekStart, weekEnd) {
  if (!t || !t.isValid()) return false;
  const d = t.startOf('day');
  return (
    !d.isBefore(weekStart.startOf('day')) && !d.isAfter(weekEnd.startOf('day'))
  );
}

/** Latest non-empty topic for a course+campus through an optional end date (inclusive). */
function getLastTopicForCourseCampusThroughDate(
  courseName,
  campusCode,
  attendanceList,
  throughDate
) {
  if (!campusCode || !courseName) return '';
  const end = throughDate ? dayjs(throughDate).endOf('day') : null;
  let bestTime = null;
  let bestTopic = '';
  for (const a of attendanceList || []) {
    if (a.campusCode !== campusCode) continue;
    if ((a.courseName || 'Unknown') !== courseName) continue;
    const t = parseAttendanceSessionStart(a);
    if (!t.isValid()) continue;
    if (end && t.isAfter(end)) continue;
    const topic = (a.topic || '').trim();
    if (!topic) continue;
    if (!bestTime || t.isAfter(bestTime)) {
      bestTime = t;
      bestTopic = topic;
    }
  }
  return bestTopic;
}

/** Latest non-empty topic for normalized subject across campuses through end date. */
function getLastTopicForSubjectKeyAcrossCampusesThroughDate(
  subjectKey,
  campusCodes,
  attendanceList,
  throughDate
) {
  if (!subjectKey || !Array.isArray(campusCodes) || campusCodes.length === 0) {
    return '';
  }
  const codeSet = new Set(campusCodes.filter(Boolean));
  const end = throughDate ? dayjs(throughDate).endOf('day') : null;
  let bestTime = null;
  let bestTopic = '';
  for (const a of attendanceList || []) {
    if (!codeSet.has(a.campusCode)) continue;
    if (normalizeCourseSubjectKey(a.courseName || '') !== subjectKey) continue;
    const t = parseAttendanceSessionStart(a);
    if (!t.isValid()) continue;
    if (end && t.isAfter(end)) continue;
    const topic = (a.topic || '').trim();
    if (!topic) continue;
    if (!bestTime || t.isAfter(bestTime)) {
      bestTime = t;
      bestTopic = topic;
    }
  }
  return bestTopic;
}

function aggregateAttendanceRowsByCourseThisWeek(
  attendanceRowsThisWeek,
  campusActiveStudentCount
) {
  const byCourse = new Map();
  for (const row of attendanceRowsThisWeek || []) {
    const key = row.courseName || 'Unknown';
    let e = byCourse.get(key);
    if (!e) {
      e = {
        classes: 0,
        lastTopicDate: null,
        lastTopicInWeek: '',
        sumPresent: 0,
        sumTotalStudents: 0,
        instructorFromRow: row.instructor || ''
      };
      byCourse.set(key, e);
    }
    e.classes += 1;
    const topic = row.topic || '';
    if (
      topic &&
      row.test &&
      row.test.isValid() &&
      (!e.lastTopicDate || row.test.isAfter(e.lastTopicDate))
    ) {
      e.lastTopicDate = row.test;
      e.lastTopicInWeek = topic;
    }
    if (row.instructor && !e.instructorFromRow) {
      e.instructorFromRow = row.instructor;
    }
    if (campusActiveStudentCount > 0) {
      e.sumPresent += (row.presentCount || 0) + (row.leaveCount || 0);
      e.sumTotalStudents += campusActiveStudentCount;
    }
  }
  return byCourse;
}

/**
 * Full campus course grid for weekly subject summary + export: every configured course,
 * plus attendance-only course names, with 0 classes this week when applicable.
 * Last topic prefers selected-week topic, else latest previous topic up to week end.
 */
function buildWeeklySubjectSummaryRows({
  campusCode,
  contextCourses,
  contextAttendance,
  attendanceRowsThisWeek,
  coursePlanByName,
  instructorById,
  campusActiveStudentCount,
  batchAnchor,
  weekEnd
}) {
  const filteredCourses = (contextCourses || []).filter(
    (c) => c.campusCodes && c.campusCodes.includes(campusCode)
  );
  const byCourse = aggregateAttendanceRowsByCourseThisWeek(
    attendanceRowsThisWeek,
    campusActiveStudentCount
  );
  const configuredNames = new Set(
    filteredCourses.map((c) => c.name).filter(Boolean)
  );

  const rows = [];

  for (const course of filteredCourses) {
    const name = course.name;
    const agg = byCourse.get(name);
    const classes = agg ? agg.classes : 0;
    const lastFromWeek = agg ? agg.lastTopicInWeek : '';
    const fallbackTopic = getLastTopicForCourseCampusThroughDate(
      name,
      campusCode,
      contextAttendance,
      weekEnd
    );
    const lastTopic =
      (lastFromWeek && String(lastFromWeek).trim()) ||
      (fallbackTopic && String(fallbackTopic).trim()) ||
      'N/A';

    const avgAttendancePct =
      agg && agg.sumTotalStudents > 0
        ? (agg.sumPresent / agg.sumTotalStudents) * 100
        : null;

    const conductedThroughWeekEnd = countConductedThroughSelectedWeek(
      name,
      campusCode,
      contextAttendance,
      batchAnchor,
      weekEnd
    );
    const plannedRaw = coursePlanByName[name];
    const hasPlan = Object.prototype.hasOwnProperty.call(
      coursePlanByName,
      name
    );
    const plannedTotal = hasPlan ? Number(plannedRaw) || 0 : null;
    const remainingClasses =
      plannedTotal !== null
        ? Math.max(0, plannedTotal - conductedThroughWeekEnd)
        : null;

    rows.push({
      subject: name,
      instructor: getDisplayInstructorName(course, instructorById) || 'N/A',
      classes,
      plannedTotal,
      conductedThroughWeekEnd,
      remainingClasses,
      lastTopic,
      avgAttendancePct
    });
  }

  for (const [name, agg] of byCourse) {
    if (configuredNames.has(name)) continue;
    const configuredInstructor = getInstructorNameForCourseName(
      name,
      contextCourses,
      instructorById
    );
    const instructor =
      (configuredInstructor && configuredInstructor !== 'Unknown'
        ? configuredInstructor
        : '') ||
      agg.instructorFromRow ||
      'N/A';

    const lastFromWeek = agg.lastTopicInWeek;
    const fallbackTopic = getLastTopicForCourseCampusThroughDate(
      name,
      campusCode,
      contextAttendance,
      weekEnd
    );
    const lastTopic =
      (lastFromWeek && String(lastFromWeek).trim()) ||
      (fallbackTopic && String(fallbackTopic).trim()) ||
      'N/A';

    const avgAttendancePct =
      agg.sumTotalStudents > 0
        ? (agg.sumPresent / agg.sumTotalStudents) * 100
        : null;

    const conductedThroughWeekEnd = countConductedThroughSelectedWeek(
      name,
      campusCode,
      contextAttendance,
      batchAnchor,
      weekEnd
    );
    const plannedRaw = coursePlanByName[name];
    const hasPlan = Object.prototype.hasOwnProperty.call(
      coursePlanByName,
      name
    );
    const plannedTotal = hasPlan ? Number(plannedRaw) || 0 : null;
    const remainingClasses =
      plannedTotal !== null
        ? Math.max(0, plannedTotal - conductedThroughWeekEnd)
        : null;

    rows.push({
      subject: name,
      instructor,
      classes: agg.classes,
      plannedTotal,
      conductedThroughWeekEnd,
      remainingClasses,
      lastTopic,
      avgAttendancePct
    });
  }

  rows.sort((a, b) => {
    if (b.classes !== a.classes) return b.classes - a.classes;
    return String(a.subject).localeCompare(String(b.subject));
  });

  return rows;
}

/**
 * One curriculum-completion row for a course at a campus (all-center report).
 * "To date" matches weekly Subject Summary: batch Week 1 through end of selected week
 * (per-campus anchor from getReportWeekStartAnchorForCampus), not all-time history.
 */
function buildCurriculumRowForCourseCampus(
  course,
  campusCode,
  attendanceList,
  weekStart,
  weekEnd,
  batchAnchorDate,
  instructorById
) {
  const courseName = course.name;
  const assigned = Number(course.totalClasses ?? 0) || 0;

  let weekCount = 0;
  let lastTopicInWeek = '';
  let lastTopicTimeInWeek = null;

  for (const a of attendanceList || []) {
    if (a.campusCode !== campusCode) continue;
    if ((a.courseName || 'Unknown') !== courseName) continue;
    const t = parseAttendanceSessionStart(a);
    if (!t.isValid()) continue;
    if (attendanceSessionDayInRange(t, weekStart, weekEnd)) {
      weekCount += 1;
      const topic = (a.topic || '').trim();
      if (topic && (!lastTopicTimeInWeek || t.isAfter(lastTopicTimeInWeek))) {
        lastTopicTimeInWeek = t;
        lastTopicInWeek = a.topic || '';
      }
    }
  }

  const toDate = countConductedThroughSelectedWeek(
    courseName,
    campusCode,
    attendanceList,
    batchAnchorDate,
    weekEnd
  );
  const remaining = Math.max(0, assigned - toDate);
  const instructor = getDisplayInstructorName(course, instructorById) || 'N/A';
  const fallbackTopic = getLastTopicForCourseCampusThroughDate(
    courseName,
    campusCode,
    attendanceList,
    weekEnd
  );

  return {
    courseName,
    assigned,
    weekCount,
    toDate,
    remaining,
    weekTopic: lastTopicInWeek || fallbackTopic || '',
    instructor
  };
}

function computeCurriculumRowsForCampus(
  campusCode,
  contextCourses,
  contextAttendance,
  weekStart,
  weekEnd,
  batchAnchorDate,
  instructorById
) {
  const courses = contextCourses.filter(
    (c) => c.campusCodes && c.campusCodes.includes(campusCode)
  );
  const rows = courses.map((course) =>
    buildCurriculumRowForCourseCampus(
      course,
      campusCode,
      contextAttendance,
      weekStart,
      weekEnd,
      batchAnchorDate,
      instructorById
    )
  );
  rows.sort((a, b) => String(a.courseName).localeCompare(String(b.courseName)));
  return rows;
}

function normalizeCampusDisplayGroupKey(campusName) {
  return String(campusName ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/** 2026–27 batch: substring in campus name or code (hyphen or underscore). */
function isCampus2026_27Batch(campus) {
  const s = `${campus.campusName || ''} ${campus.campusCode || ''}`.toLowerCase();
  return (
    s.includes('2026-27') ||
    s.includes('2026_27') ||
    s.includes('2026/27')
  );
}

/** All campuses from Firestore when available; ignores allowedCampus filter. Then 2026-27 only. */
function campusesForConsolidatedReport(campusesAllUnfiltered, campusesFallback) {
  const pool =
    campusesAllUnfiltered && campusesAllUnfiltered.length > 0
      ? campusesAllUnfiltered
      : campusesFallback || [];
  return pool.filter(isCampus2026_27Batch);
}

/**
 * Group campuses that share the same display name (after normalize) so duplicate
 * Firestore rows (same name, different id/code) roll up into one logical center.
 * Rows with blank names are not merged: each uses its own campusCode as the key.
 */
function buildConsolidatedCampusClusters(campuses) {
  const byKey = new Map();
  for (const c of campuses || []) {
    const code = c.campusCode;
    if (!code) continue;
    const nameKey = normalizeCampusDisplayGroupKey(c.campusName);
    const clusterKey = nameKey || `_code_${code}`;
    if (!byKey.has(clusterKey)) {
      byKey.set(clusterKey, {
        clusterKey,
        displayName:
          (c.campusName && String(c.campusName).trim()) || String(code),
        campuses: []
      });
    }
    byKey.get(clusterKey).campuses.push(c);
  }
  const list = [...byKey.values()];
  for (const cl of list) {
    cl.campuses.sort((a, b) =>
      String(a.campusCode || '').localeCompare(String(b.campusCode || ''))
    );
    cl.codes = [...new Set(cl.campuses.map((x) => x.campusCode).filter(Boolean))];
  }
  list.sort((a, b) =>
    String(a.displayName).localeCompare(String(b.displayName))
  );
  return list;
}

/** One merged course per normalized subject: planned = max(totalClasses) across duplicate rows / spellings. */
function mergeCoursesForClusterCodes(codes, contextCourses) {
  const codeSet = new Set(codes);
  const relevant = contextCourses.filter(
    (c) => c.campusCodes && c.campusCodes.some((cc) => codeSet.has(cc))
  );
  const byKey = new Map();
  for (const c of relevant) {
    const subjectKey = normalizeCourseSubjectKey(c.name);
    if (!subjectKey) continue;
    if (!byKey.has(subjectKey)) byKey.set(subjectKey, []);
    byKey.get(subjectKey).push(c);
  }
  const merged = [];
  for (const [subjectKey, arr] of byKey) {
    const assigned = arr.length
      ? Math.max(...arr.map((c) => Number(c.totalClasses ?? 0) || 0))
      : 0;
    const primaryCourse = [...arr].sort((a, b) =>
      String(a.id || '').localeCompare(String(b.id || ''))
    )[0];
    const courseName = canonicalCourseDisplayName(arr.map((x) => x.name));
    merged.push({ subjectKey, courseName, assigned, primaryCourse });
  }
  merged.sort((a, b) =>
    String(a.courseName).localeCompare(String(b.courseName))
  );
  return merged;
}

function computeCurriculumRowsForCluster(
  cluster,
  contextCourses,
  contextAttendance,
  weekStart,
  weekEnd,
  instructorById
) {
  const mergedCourses = mergeCoursesForClusterCodes(cluster.codes, contextCourses);
  return mergedCourses.map((merged) =>
    buildCurriculumRowForClusterCourse(
      merged,
      cluster,
      contextAttendance,
      weekStart,
      weekEnd,
      instructorById
    )
  );
}

function buildCurriculumRowForClusterCourse(
  merged,
  cluster,
  attendanceList,
  weekStart,
  weekEnd,
  instructorById
) {
  const courseName = merged.courseName;
  const subjectKey = merged.subjectKey;
  const assigned = merged.assigned;
  const primaryCourse = merged.primaryCourse;
  const codes = cluster.codes;

  let weekCount = 0;
  let lastTopicInWeek = '';
  let lastTopicTimeInWeek = null;

  for (const a of attendanceList || []) {
    if (!codes.includes(a.campusCode)) continue;
    if (normalizeCourseSubjectKey(a.courseName || '') !== subjectKey) continue;
    const t = parseAttendanceSessionStart(a);
    if (!t.isValid()) continue;
    if (attendanceSessionDayInRange(t, weekStart, weekEnd)) {
      weekCount += 1;
      const topic = (a.topic || '').trim();
      if (topic && (!lastTopicTimeInWeek || t.isAfter(lastTopicTimeInWeek))) {
        lastTopicTimeInWeek = t;
        lastTopicInWeek = a.topic || '';
      }
    }
  }

  let toDate = 0;
  for (const campus of cluster.campuses) {
    if (!campus.campusCode) continue;
    const anchor = getReportWeekStartAnchorForCampus(campus);
    toDate += countConductedForSubjectKey(
      subjectKey,
      campus.campusCode,
      attendanceList,
      anchor,
      weekEnd
    );
  }

  const remaining = Math.max(0, assigned - toDate);
  const instructor =
    getDisplayInstructorName(primaryCourse, instructorById) || 'N/A';
  const fallbackTopic = getLastTopicForSubjectKeyAcrossCampusesThroughDate(
    subjectKey,
    codes,
    attendanceList,
    weekEnd
  );

  return {
    courseName,
    assigned,
    weekCount,
    toDate,
    remaining,
    weekTopic: lastTopicInWeek || fallbackTopic || '',
    instructor
  };
}

/**
 * Uniform class budget per campus in the consolidated summary block (same for every row).
 * بقیہ کلاسز = this minus Σ(to date) for that campus. Change here if the annual target changes.
 */
const CONSOLIDATED_PER_CAMPUS_CLASS_TOTAL = 782;

/** Expected classes per week for consolidated summary (ضائع = shortfall vs this target). */
const CONSOLIDATED_CLASSES_PER_WEEK = 19;

/** Pastel row fills for consolidated “center totals” block (cycles if more campuses). */
const CONSOLIDATED_CAMPUS_SUMMARY_ROW_FILLS = [
  'DDEBF7',
  'FCE4EC',
  'F4B084',
  'E2EFDA',
  'E4DFEC',
  'FCE5CD',
  'D9E1F2'
];

function hashStringToUint32(str) {
  let h = 2166136261;
  const s = String(str ?? '');
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** HSL (h 0–360, s/l 0–100) → RGB for pastel fills. */
function hslToRgbHex(h, s, l) {
  const hh = ((h % 360) + 360) % 360;
  const ss = Math.min(100, Math.max(0, s)) / 100;
  const ll = Math.min(100, Math.max(0, l)) / 100;
  const hue2rgb = (p, q, t) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  let r;
  let g;
  let b;
  if (ss === 0) {
    r = g = b = ll;
  } else {
    const q = ll < 0.5 ? ll * (1 + ss) : ll + ss - ll * ss;
    const p = 2 * ll - q;
    r = hue2rgb(p, q, hh / 360 + 1 / 3);
    g = hue2rgb(p, q, hh / 360);
    b = hue2rgb(p, q, hh / 360 - 1 / 3);
  }
  const toHex = (x) =>
    Math.min(255, Math.max(0, Math.round(x * 255)))
      .toString(16)
      .padStart(2, '0')
      .toUpperCase();
  return `${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/** Stable pastel background per course name (consolidated XLSX subject column only). */
function consolidatedCourseNameCellStyle(baseLeftStyle, courseName) {
  const seed = hashStringToUint32(courseName);
  const h = seed % 360;
  const s = 32 + ((seed >>> 8) % 14);
  const l = 86 + ((seed >>> 16) % 9);
  const fillRgb = hslToRgbHex(h, s, l);
  const baseFont = baseLeftStyle.font || { sz: 10 };
  return {
    ...baseLeftStyle,
    fill: { fgColor: { rgb: fillRgb } },
    font: {
      ...baseFont,
      bold: true,
      color: { rgb: '263238' }
    }
  };
}

/** Excel worksheet name: max 31 chars, no [] * ? : / \\ */
function safeExcelSheetName(raw, usedNames) {
  let s = String(raw || 'Sheet')
    .replace(/[\\/*?:[\]]/g, '_')
    .replace(/'/g, '')
    .trim()
    .slice(0, 31);
  if (!s) s = 'Sheet';
  let candidate = s;
  let n = 1;
  while (usedNames.has(candidate)) {
    const suffix = `~${n}`;
    candidate = `${s.slice(0, Math.max(1, 31 - suffix.length))}${suffix}`;
    n += 1;
  }
  usedNames.add(candidate);
  return candidate;
}

/** Shared green/blue theme and helpers for Reports XLSX exports (matches Weekly Report styling). */
function createReportsThemeXlsx() {
  const thin = (rgb = 'CCCCCC') => ({ style: 'thin', color: { rgb } });
  const medium = (rgb = '000000') => ({ style: 'medium', color: { rgb } });
  const allBorders = (b = thin()) => ({ top: b, bottom: b, left: b, right: b });

  const titleStyle = {
    font: { bold: true, sz: 18, color: { rgb: 'FFFFFF' } },
    fill: { fgColor: { rgb: '2E7D32' } },
    alignment: { horizontal: 'left', vertical: 'center', indent: 1 },
    border: allBorders(medium('1B5E20'))
  };
  const subtitleStyle = {
    font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } },
    fill: { fgColor: { rgb: '4CAF50' } },
    alignment: { horizontal: 'left', vertical: 'center', indent: 1 },
    border: allBorders(thin('A5D6A7'))
  };
  const generatedStyle = {
    font: { italic: true, sz: 10, color: { rgb: 'FFFFFF' } },
    fill: { fgColor: { rgb: '4CAF50' } },
    alignment: { horizontal: 'right', vertical: 'center', indent: 1 },
    border: allBorders(thin('A5D6A7'))
  };
  const sectionHeadingStyle = {
    font: { bold: true, sz: 13, color: { rgb: '1B5E20' } },
    alignment: { horizontal: 'left', vertical: 'center' }
  };
  const kpiLabelStyle = {
    font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } },
    fill: { fgColor: { rgb: '2E7D32' } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: allBorders(medium('1B5E20'))
  };
  const kpiValueStyle = {
    font: { bold: true, sz: 22, color: { rgb: '1B5E20' } },
    fill: { fgColor: { rgb: 'E8F5E9' } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: allBorders(medium('1B5E20'))
  };
  const tableHeaderStyle = {
    font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } },
    fill: { fgColor: { rgb: '4472C4' } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: allBorders(thin('1F3864'))
  };
  const cellBaseStyle = {
    font: { sz: 10 },
    alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
    border: allBorders()
  };
  const cellCenterStyle = {
    ...cellBaseStyle,
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true }
  };
  const altRowFill = { fgColor: { rgb: 'F2F2F2' } };
  const dayBandStyle = {
    font: { bold: true, sz: 11, color: { rgb: '1B5E20' } },
    fill: { fgColor: { rgb: 'E8F5E9' } },
    alignment: { horizontal: 'left', vertical: 'center', indent: 1 },
    border: allBorders(thin('A5D6A7'))
  };

  const attendanceCellStyle = (pct) => {
    let bg = 'F2F2F2';
    let fg = '000000';
    if (pct !== null && !isNaN(pct)) {
      if (pct > ATTENDANCE_PCT_GOOD_ABOVE) {
        bg = 'C6EFCE';
        fg = '006100';
      } else if (pct >= ATTENDANCE_PCT_WARN_MIN) {
        bg = 'FFEB9C';
        fg = '7F6000';
      } else {
        bg = 'F44336';
        fg = 'FFFFFF';
      }
    }
    return {
      font: { bold: true, sz: 10, color: { rgb: fg } },
      fill: { fgColor: { rgb: bg } },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: allBorders()
    };
  };

  const setCell = (ws, addr, value, style) => {
    const isNumber = typeof value === 'number' && !Number.isNaN(value);
    ws[addr] = { v: value === null || value === undefined ? '' : value, t: isNumber ? 'n' : 's' };
    if (style) ws[addr].s = style;
  };

  const ensureRange = (ws, lastRow, lastCol) => {
    ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: lastRow, c: lastCol } });
  };

  /** Topic / comment: Noto + RTL-friendly alignment when Urdu/Arabic script is present. */
  const xlsxTopicStyle = (baseStyle, text) => {
    const raw = String(text ?? '');
    if (!raw.trim()) return baseStyle;
    if (!isUrduText(raw)) return baseStyle;
    const baseFont = baseStyle.font || { sz: 10 };
    const sz = Math.max(Number(baseFont.sz) || 10, 12);
    return {
      ...baseStyle,
      font: {
        ...baseFont,
        name: 'Noto Nastaliq Urdu',
        sz
      },
      alignment: {
        ...(baseStyle.alignment || {}),
        horizontal: 'right',
        vertical: 'center',
        wrapText: true,
        readingOrder: 2
      }
    };
  };

  return {
    thin,
    medium,
    allBorders,
    titleStyle,
    subtitleStyle,
    generatedStyle,
    sectionHeadingStyle,
    kpiLabelStyle,
    kpiValueStyle,
    tableHeaderStyle,
    cellBaseStyle,
    cellCenterStyle,
    altRowFill,
    dayBandStyle,
    attendanceCellStyle,
    setCell,
    ensureRange,
    xlsxTopicStyle
  };
}

const ReportsList = () => {
  const navigate = useNavigate();
  
  // Get data from context
  const {
    courses: contextCourses,
    attendance: contextAttendance,
    campuses: contextCampuses,
    campusesAllUnfiltered: contextCampusesAllUnfiltered,
    instructors: contextInstructors,
    students: contextStudents,
    fetchAllAttendance,
    initialLoadComplete
  } = useFirebaseData();

  const instructorById = useMemo(
    () => buildInstructorByIdMap(contextInstructors),
    [contextInstructors]
  );

  const consolidatedCampusPool = useMemo(
    () =>
      campusesForConsolidatedReport(
        contextCampusesAllUnfiltered,
        contextCampuses
      ),
    [contextCampusesAllUnfiltered, contextCampuses]
  );

  const consolidatedCampusClustersPreview = useMemo(
    () => buildConsolidatedCampusClusters(consolidatedCampusPool),
    [consolidatedCampusPool]
  );

  // State variables
  const [courseTableData, setCourseTableData] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState('');
  const [weekOptions, setWeekOptions] = useState([]);
  const [attendanceTableData, setAttendanceTableData] = useState([]);
  const [activeTab, setActiveTab] = useState(1);
  const [selectedCourseForReport, setSelectedCourseForReport] = useState('');
  const [selectedCampus, setSelectedCampus] = useState('');
  const [consolidatedAttendance, setConsolidatedAttendance] = useState([]);
  const [loadingConsolidatedData, setLoadingConsolidatedData] = useState(false);

  const consolidatedReportAttendance = consolidatedAttendance.length > 0
    ? consolidatedAttendance
    : contextAttendance;

  const loadConsolidatedData = useCallback(async () => {
    if (consolidatedAttendance.length > 0) {
      return consolidatedAttendance;
    }
    if (loadingConsolidatedData) {
      return consolidatedReportAttendance;
    }

    setLoadingConsolidatedData(true);
    try {
      const allAttendance = await fetchAllAttendance();
      setConsolidatedAttendance(allAttendance);
      return allAttendance;
    } catch (err) {
      console.error('Error loading consolidated report data:', err);
      return consolidatedReportAttendance;
    } finally {
      setLoadingConsolidatedData(false);
    }
  }, [consolidatedAttendance, consolidatedReportAttendance, fetchAllAttendance, loadingConsolidatedData]);

  useEffect(() => {
    if (activeTab === 0 && initialLoadComplete) {
      loadConsolidatedData();
    }
  }, [activeTab, initialLoadComplete, loadConsolidatedData]);

  const campusActiveStudentCount = useMemo(
    () => getCampusActiveStudentCount(contextStudents, selectedCampus),
    [contextStudents, selectedCampus]
  );

  const reportWeekStartAnchor = useMemo(() => {
    if (!selectedCampus) return REPORT_WEEK_ANCHOR_DEFAULT;
    const campus = contextCampuses.find(c => c.campusCode === selectedCampus);
    return getReportWeekStartAnchorForCampus(campus);
  }, [selectedCampus, contextCampuses]);

  // Handle modify button click - navigate to attendance page with date
  const handleModify = (dateStr) => {
    // dateStr is in format 'DD/MM/YYYY', pass as-is (CourseSelector now handles this format)
    navigate(`/attendance?date=${dateStr}`);
  };

  // Week options from REPORT_BATCH_WEEK_ANCHOR_RULES + default anchor
  useEffect(() => {
    const generateWeeksFromDate = (startDate) => {
      const weeks = [];
      let currentDate = dayjs(startDate).startOf('isoWeek');

      for (let weekNumber = 1; weekNumber <= 52; weekNumber++) {
        const startOfWeek = currentDate;
        const endOfWeek = currentDate.endOf('isoWeek');
        const weekLabel = `${startOfWeek.format('MMM D')} - ${endOfWeek.format('MMM D, YYYY')}`;

        weeks.push({
          weekNumber,
          label: weekLabel,
          startDate: startOfWeek,
          endDate: endOfWeek,
          year: parseInt(endOfWeek.format('YYYY'))
        });

        currentDate = currentDate.add(1, 'week');
      }

      return weeks;
    };

    setWeekOptions(generateWeeksFromDate(reportWeekStartAnchor));
    setSelectedWeek('');
  }, [reportWeekStartAnchor]);

  const courseMap = useMemo(
    () => buildCourseNameToInstructorMap(contextCourses, instructorById, selectedCampus),
    [contextCourses, instructorById, selectedCampus]
  );

  // Process attendance data from context
  const cloneData = useMemo(() => {
    if (!initialLoadComplete || !selectedCampus) return [];

    // Filter attendance by selected campus
    const attendanceData = contextAttendance.filter(a => a.campusCode === selectedCampus);

    const rows = attendanceData.map(d => {
      // Format is: YYYY-MM-DD-HH:mm (e.g., 2025-05-06-12:00)
      const start = dayjs(d.startTime?.replace(/-(\d{1,2}):(\d{2})$/, ' $1:$2'), 'YYYY-MM-DD HH:mm');
      const end = dayjs(d.endTime?.replace(/-(\d{1,2}):(\d{2})$/, ' $1:$2'), 'YYYY-MM-DD HH:mm');
      const { presentCount, absentCount, leaveCount } = dedupeAttendanceBuckets(d);

      return {
        id: d.id,
        date: start.isValid() ? start.format('DD/MM/YYYY') : 'N/A',
        courseName: d.courseName,
        instructor: courseMap[d.courseName] || 'Unknown',
        startTime: start.isValid() ? start.format('hh:mm A') : 'N/A',
        endTime: end.isValid() ? end.format('hh:mm A') : 'N/A',
        topic: d.topic,
        comments: d.comments,
        test: start,
        presentCount,
        absentCount,
        leaveCount
      };
    });

    // Sort by date in ascending order
    return rows.filter(r => r.test.isValid()).sort((a, b) => a.test - b.test);
  }, [contextAttendance, selectedCampus, courseMap, initialLoadComplete]);

  // Process course summary from context data
  const courseSummaryData = useMemo(() => {
    if (!initialLoadComplete || !selectedCampus) return [];

    // Filter attendance by selected campus
    const attendanceData = contextAttendance.filter(a => a.campusCode === selectedCampus);
    
    // Filter courses by selected campus
    const filteredCourses = contextCourses.filter(course => 
      course.campusCodes && course.campusCodes.includes(selectedCampus)
    );

    return filteredCourses.map(course => {
      const courseAttendance = attendanceData.filter(a => a.courseName === course.name);
      
      // Find the last topic covered for this course
      let lastTopic = 'N/A';
      if (courseAttendance.length > 0) {
        // Sort attendance records by date (most recent first)
        const sortedAttendance = courseAttendance
          .map(a => {
            const date = dayjs(a.startTime?.replace(/-(\d{1,2}):(\d{2})$/, ' $1:$2'), 'YYYY-MM-DD HH:mm');
            return {
              date: date,
              topic: a.topic || '',
              valid: date.isValid()
            };
          })
          .filter(item => item.valid)
          .sort((a, b) => b.date - a.date); // Sort descending (most recent first)
        
        // Get the topic from the most recent attendance record
        if (sortedAttendance.length > 0 && sortedAttendance[0].topic) {
          lastTopic = sortedAttendance[0].topic;
        }
      }

      const displayName = getDisplayInstructorName(course, instructorById);
      return {
        courseName: course.name,
        totalClasses: course.totalClasses || 0,
        totalClassesConducted: courseAttendance.length,
        remainingClasses: (course.totalClasses || 0) - courseAttendance.length,
        instructor: displayName || 'N/A',
        lastTopic: lastTopic
      };
    });
  }, [contextCourses, contextAttendance, selectedCampus, initialLoadComplete, instructorById]);

  /** Course name → planned session count from course config (selected campus only). */
  const coursePlanByName = useMemo(() => {
    if (!selectedCampus) return {};
    const m = Object.create(null);
    contextCourses
      .filter(
        (c) => c.campusCodes && c.campusCodes.includes(selectedCampus)
      )
      .forEach((c) => {
        if (c.name != null) m[c.name] = Number(c.totalClasses ?? 0) || 0;
      });
    return m;
  }, [contextCourses, selectedCampus]);

  // Update attendance table data when clone data changes or week selection changes
  useEffect(() => {
    if (!initialLoadComplete || !selectedCampus) {
      setAttendanceTableData([]);
      return;
    }

    if (!selectedWeek) {
      setAttendanceTableData(cloneData);
      return;
    }

    const week = weekOptions.find(w => w.weekNumber === selectedWeek);
    if (!week || cloneData.length === 0) {
      setAttendanceTableData([]);
      return;
    }

    const filtered = cloneData.filter(row => {
      const attendanceDate = row.test;
      return attendanceDate.isValid() && attendanceDate.isBetween(week.startDate, week.endDate, 'day', '[]');
    });

    setAttendanceTableData(filtered.sort((a, b) => a.test - b.test));
  }, [cloneData, selectedWeek, weekOptions, initialLoadComplete, selectedCampus]);

  // Update course table data when context data changes
  useEffect(() => {
    if (initialLoadComplete) {
      setCourseTableData(courseSummaryData);
    }
  }, [courseSummaryData, initialLoadComplete]);

  // Filter by selected week
  const handleWeekChange = event => {
    const weekNum = event.target.value;
    setSelectedWeek(weekNum);
  };

  // Calculate summary totals
  const calculateSummary = () => {
    const totalClasses = courseTableData.reduce((sum, course) => sum + (course.totalClasses || 0), 0);
    const totalConducted = courseTableData.reduce((sum, course) => sum + (course.totalClassesConducted || 0), 0);
    const totalRemaining = courseTableData.reduce((sum, course) => sum + (course.remainingClasses || 0), 0);
    
    return { totalClasses, totalConducted, totalRemaining };
  };

  // Export Weekly Report as a styled XLSX workbook (Summary + Daily Detail)
  const handleExportWeeklyReport = () => {
    const week = weekOptions.find(w => w.weekNumber === selectedWeek);
    if (!week || !selectedCampus) {
      return;
    }

    const campus = contextCampuses.find(c => c.campusCode === selectedCampus);
    const batchAnchor = getReportWeekStartAnchorForCampus(campus);
    const campusName = campus?.campusName || 'Unknown Campus';
    const campusCode = campus?.campusCode || selectedCampus || 'N/A';
    const weekLabelText = `Week ${week.weekNumber}: ${week.label}`;
    const generatedOn = dayjs().format('DD MMM YYYY, hh:mm A');

    const {
      titleStyle,
      subtitleStyle,
      generatedStyle,
      sectionHeadingStyle,
      kpiLabelStyle,
      kpiValueStyle,
      tableHeaderStyle,
      cellBaseStyle,
      cellCenterStyle,
      altRowFill,
      dayBandStyle,
      attendanceCellStyle,
      setCell,
      ensureRange,
      xlsxTopicStyle,
      medium,
      allBorders
    } = createReportsThemeXlsx();

    const weeklySubtotalLabelStyle = {
      font: { bold: true, sz: 11, color: { rgb: '1B5E20' } },
      fill: { fgColor: { rgb: 'C8E6C9' } },
      alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
      border: allBorders(medium('1B5E20'))
    };
    const weeklySubtotalNumberStyle = {
      font: { bold: true, sz: 11 },
      fill: { fgColor: { rgb: 'C8E6C9' } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border: allBorders(medium('1B5E20'))
    };

    // ===========================
    // Sheet 1: Summary
    // ===========================
    const summaryWs = {};
    const summaryMerges = [];

    const subjectSummaryRows = buildWeeklySubjectSummaryRows({
      campusCode: selectedCampus,
      contextCourses,
      contextAttendance,
      attendanceRowsThisWeek: attendanceTableData,
      coursePlanByName,
      instructorById,
      campusActiveStudentCount,
      batchAnchor,
      weekEnd: week.endDate
    });

    if (
      subjectSummaryRows.length === 0 &&
      (!attendanceTableData || attendanceTableData.length === 0)
    ) {
      return;
    }

    const totalClassesThisWeek = attendanceTableData.length;
    const campusCoursesListed = subjectSummaryRows.length;
    const coursesWithClassThisWeek = new Set(
      attendanceTableData.map((r) => r.courseName)
    ).size;
    const daysWithClasses = new Set(attendanceTableData.map(r => r.date)).size;

    // Layout — 8 columns on the summary sheet (subject table)
    const SUMMARY_COLS = 8;

    // Row 0: Title (merged across all columns)
    setCell(summaryWs, 'A1', `Weekly Report — ${campusName} (${campusCode})`, titleStyle);
    for (let c = 1; c < SUMMARY_COLS; c++) {
      setCell(summaryWs, XLSX.utils.encode_cell({ r: 0, c }), '', titleStyle);
    }
    summaryMerges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: SUMMARY_COLS - 1 } });

    // Row 1: Subtitle (week label) split with generated-on aligned right
    setCell(summaryWs, 'A2', weekLabelText, subtitleStyle);
    for (let c = 1; c < 4; c++) {
      setCell(summaryWs, XLSX.utils.encode_cell({ r: 1, c }), '', subtitleStyle);
    }
    summaryMerges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: 3 } });
    setCell(summaryWs, 'E2', `Generated: ${generatedOn}`, generatedStyle);
    for (let c = 5; c < SUMMARY_COLS; c++) {
      setCell(summaryWs, XLSX.utils.encode_cell({ r: 1, c }), '', generatedStyle);
    }
    summaryMerges.push({ s: { r: 1, c: 4 }, e: { r: 1, c: SUMMARY_COLS - 1 } });

    // Row 2: Spacer (set tiny row height by leaving blank)
    let curRow = 3;

    // KPI heading
    setCell(summaryWs, XLSX.utils.encode_cell({ r: curRow, c: 0 }), 'Overview', sectionHeadingStyle);
    curRow += 1;

    // KPI block: one row of labels + one row of values (two columns per KPI)
    const kpis = [
      { label: 'Total Classes', value: totalClassesThisWeek },
      { label: 'Campus Courses', value: campusCoursesListed },
      {
        label: 'Courses With Class',
        value: coursesWithClassThisWeek
      },
      { label: 'Days With Classes', value: daysWithClasses }
    ];
    kpis.forEach((kpi, i) => {
      const startCol = i * 2;
      // Label row
      setCell(summaryWs, XLSX.utils.encode_cell({ r: curRow, c: startCol }), kpi.label, kpiLabelStyle);
      setCell(summaryWs, XLSX.utils.encode_cell({ r: curRow, c: startCol + 1 }), '', kpiLabelStyle);
      summaryMerges.push({ s: { r: curRow, c: startCol }, e: { r: curRow, c: startCol + 1 } });
      // Value row
      setCell(summaryWs, XLSX.utils.encode_cell({ r: curRow + 1, c: startCol }), kpi.value, kpiValueStyle);
      setCell(summaryWs, XLSX.utils.encode_cell({ r: curRow + 1, c: startCol + 1 }), '', kpiValueStyle);
      summaryMerges.push({ s: { r: curRow + 1, c: startCol }, e: { r: curRow + 1, c: startCol + 1 } });
    });
    curRow += 2;

    // Spacer row
    curRow += 1;

    // Subject summary heading
    setCell(summaryWs, XLSX.utils.encode_cell({ r: curRow, c: 0 }), 'Subject Summary', sectionHeadingStyle);
    curRow += 1;

    const subjectHeaders = [
      'Subject',
      'Instructor',
      'This week',
      'Planned',
      'To date',
      'Remaining',
      'Last Topic',
      'Avg. Attendance'
    ];
    subjectHeaders.forEach((h, i) => {
      setCell(summaryWs, XLSX.utils.encode_cell({ r: curRow, c: i }), h, tableHeaderStyle);
    });
    curRow += 1;

    let subSumThisWeek = 0;
    let subSumToDate = 0;
    let subSumPlanned = 0;
    let subSumRemaining = 0;
    let subPlanComplete = subjectSummaryRows.length > 0;

    subjectSummaryRows.forEach((row, idx) => {
      const isAlt = idx % 2 === 1;
      const baseFill = isAlt ? altRowFill : { fgColor: { rgb: 'FFFFFF' } };
      const cellStyle = { ...cellBaseStyle, fill: baseFill };
      const centerStyle = { ...cellCenterStyle, fill: baseFill };

      subSumThisWeek += row.classes;
      subSumToDate += row.conductedThroughWeekEnd;
      if (row.plannedTotal !== null) subSumPlanned += row.plannedTotal;
      else subPlanComplete = false;
      if (row.remainingClasses !== null) subSumRemaining += row.remainingClasses;
      else subPlanComplete = false;

      setCell(summaryWs, XLSX.utils.encode_cell({ r: curRow, c: 0 }), row.subject, cellStyle);
      setCell(summaryWs, XLSX.utils.encode_cell({ r: curRow, c: 1 }), row.instructor, cellStyle);
      setCell(summaryWs, XLSX.utils.encode_cell({ r: curRow, c: 2 }), row.classes, centerStyle);
      setCell(
        summaryWs,
        XLSX.utils.encode_cell({ r: curRow, c: 3 }),
        row.plannedTotal !== null ? row.plannedTotal : '—',
        centerStyle
      );
      setCell(
        summaryWs,
        XLSX.utils.encode_cell({ r: curRow, c: 4 }),
        row.conductedThroughWeekEnd,
        centerStyle
      );
      setCell(
        summaryWs,
        XLSX.utils.encode_cell({ r: curRow, c: 5 }),
        row.remainingClasses !== null ? row.remainingClasses : '—',
        centerStyle
      );
      setCell(
        summaryWs,
        XLSX.utils.encode_cell({ r: curRow, c: 6 }),
        row.lastTopic,
        xlsxTopicStyle(cellStyle, row.lastTopic)
      );
      const pct = row.avgAttendancePct;
      const pctText = pct === null || isNaN(pct) ? '—' : `${pct.toFixed(1)}%`;
      setCell(summaryWs, XLSX.utils.encode_cell({ r: curRow, c: 7 }), pctText, attendanceCellStyle(pct));
      curRow += 1;
    });

    if (subjectSummaryRows.length > 0) {
      setCell(
        summaryWs,
        XLSX.utils.encode_cell({ r: curRow, c: 0 }),
        'Subtotal / ذیلی کل',
        weeklySubtotalLabelStyle
      );
      setCell(
        summaryWs,
        XLSX.utils.encode_cell({ r: curRow, c: 1 }),
        '',
        weeklySubtotalLabelStyle
      );
      setCell(
        summaryWs,
        XLSX.utils.encode_cell({ r: curRow, c: 2 }),
        subSumThisWeek,
        weeklySubtotalNumberStyle
      );
      setCell(
        summaryWs,
        XLSX.utils.encode_cell({ r: curRow, c: 3 }),
        subPlanComplete ? subSumPlanned : '—',
        weeklySubtotalNumberStyle
      );
      setCell(
        summaryWs,
        XLSX.utils.encode_cell({ r: curRow, c: 4 }),
        subSumToDate,
        weeklySubtotalNumberStyle
      );
      setCell(
        summaryWs,
        XLSX.utils.encode_cell({ r: curRow, c: 5 }),
        subPlanComplete ? subSumRemaining : '—',
        weeklySubtotalNumberStyle
      );
      setCell(
        summaryWs,
        XLSX.utils.encode_cell({ r: curRow, c: 6 }),
        '',
        weeklySubtotalLabelStyle
      );
      setCell(
        summaryWs,
        XLSX.utils.encode_cell({ r: curRow, c: 7 }),
        '',
        weeklySubtotalLabelStyle
      );
      curRow += 1;
    }

    summaryWs['!cols'] = [
      { wch: 26 },
      { wch: 20 },
      { wch: 11 },
      { wch: 11 },
      { wch: 11 },
      { wch: 11 },
      { wch: 34 },
      { wch: 16 }
    ];
    summaryWs['!merges'] = summaryMerges;
    summaryWs['!rows'] = [
      { hpt: 32 }, // Title
      { hpt: 22 }, // Subtitle
      { hpt: 8 }   // Spacer
    ];
    ensureRange(summaryWs, curRow - 1, SUMMARY_COLS - 1);

    // ===========================
    // Sheet 2: Daily Detail
    // ===========================
    const detailWs = {};
    const detailMerges = [];
    const DETAIL_COLS = 11;
    const detailHeaders = [
      'Date', 'Day', 'Time', 'Course', 'Instructor', 'Topic',
      'Comments', 'Present', 'Absent', 'Leaves', 'Attendance %'
    ];

    // Title band
    setCell(detailWs, 'A1', `Daily Detail — ${weekLabelText}`, titleStyle);
    for (let c = 1; c < DETAIL_COLS; c++) {
      setCell(detailWs, XLSX.utils.encode_cell({ r: 0, c }), '', titleStyle);
    }
    detailMerges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: DETAIL_COLS - 1 } });

    // Subtitle (campus + generated)
    setCell(detailWs, 'A2', `${campusName} (${campusCode})`, subtitleStyle);
    for (let c = 1; c < 7; c++) {
      setCell(detailWs, XLSX.utils.encode_cell({ r: 1, c }), '', subtitleStyle);
    }
    detailMerges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: 6 } });
    setCell(detailWs, XLSX.utils.encode_cell({ r: 1, c: 7 }), `Generated: ${generatedOn}`, generatedStyle);
    for (let c = 8; c < DETAIL_COLS; c++) {
      setCell(detailWs, XLSX.utils.encode_cell({ r: 1, c }), '', generatedStyle);
    }
    detailMerges.push({ s: { r: 1, c: 7 }, e: { r: 1, c: DETAIL_COLS - 1 } });

    let dRow = 3;

    // Column headers
    detailHeaders.forEach((h, i) => {
      setCell(detailWs, XLSX.utils.encode_cell({ r: dRow, c: i }), h, tableHeaderStyle);
    });
    dRow += 1;

    // Group rows by date (preserve chronological order from attendanceTableData)
    const groupedForExport = (() => {
      const grouped = {};
      attendanceTableData.forEach(row => {
        const date = row.date;
        if (!grouped[date]) {
          grouped[date] = {
            date,
            dayOfWeek: row.test && row.test.isValid() ? row.test.format('dddd') : '',
            sortKey: row.test && row.test.isValid() ? row.test.valueOf() : 0,
            classes: []
          };
        }
        grouped[date].classes.push(row);
      });
      return Object.values(grouped).sort((a, b) => a.sortKey - b.sortKey);
    })();

    let bodyRowIndex = 0;
    groupedForExport.forEach(dayGroup => {
      // Day band row
      const bandLabel = `${dayGroup.dayOfWeek} — ${dayGroup.date}  (${dayGroup.classes.length} ${dayGroup.classes.length === 1 ? 'class' : 'classes'})`;
      setCell(detailWs, XLSX.utils.encode_cell({ r: dRow, c: 0 }), bandLabel, dayBandStyle);
      for (let c = 1; c < DETAIL_COLS; c++) {
        setCell(detailWs, XLSX.utils.encode_cell({ r: dRow, c }), '', dayBandStyle);
      }
      detailMerges.push({ s: { r: dRow, c: 0 }, e: { r: dRow, c: DETAIL_COLS - 1 } });
      dRow += 1;

      dayGroup.classes.forEach(row => {
        const isAlt = bodyRowIndex % 2 === 1;
        const baseFill = isAlt ? altRowFill : { fgColor: { rgb: 'FFFFFF' } };
        const cellStyle = { ...cellBaseStyle, fill: baseFill };
        const centerStyle = { ...cellCenterStyle, fill: baseFill };

        const present = row.presentCount || 0;
        const absent = row.absentCount || 0;
        const leave = row.leaveCount || 0;
        const pct = sessionPresentPct(
          present + leave,
          campusActiveStudentCount
        );
        const pctText = pct === null || isNaN(pct) ? '—' : `${pct.toFixed(1)}%`;

        const cells = [
          { v: row.date || '', s: centerStyle },
          { v: row.test && row.test.isValid() ? row.test.format('dddd') : '', s: centerStyle },
          { v: `${row.startTime || ''} - ${row.endTime || ''}`, s: centerStyle },
          { v: row.courseName || '', s: cellStyle },
          { v: row.instructor || '', s: cellStyle },
          { v: row.topic || '', s: xlsxTopicStyle(cellStyle, row.topic) },
          { v: row.comments || '', s: xlsxTopicStyle(cellStyle, row.comments) },
          { v: present, s: centerStyle },
          { v: absent, s: centerStyle },
          { v: leave, s: centerStyle },
          { v: pctText, s: attendanceCellStyle(pct) }
        ];
        cells.forEach((cell, i) => {
          setCell(detailWs, XLSX.utils.encode_cell({ r: dRow, c: i }), cell.v, cell.s);
        });
        dRow += 1;
        bodyRowIndex += 1;
      });
    });

    detailWs['!cols'] = [
      { wch: 12 }, // Date
      { wch: 11 }, // Day
      { wch: 18 }, // Time
      { wch: 24 }, // Course
      { wch: 22 }, // Instructor
      { wch: 38 }, // Topic
      { wch: 30 }, // Comments
      { wch: 10 }, // Present
      { wch: 10 }, // Absent
      { wch: 10 }, // Leaves
      { wch: 14 }  // Attendance %
    ];
    detailWs['!merges'] = detailMerges;
    detailWs['!rows'] = [
      { hpt: 32 }, // Title
      { hpt: 20 }  // Subtitle
    ];
    detailWs['!freeze'] = { xSplit: 0, ySplit: 4 };
    ensureRange(detailWs, dRow - 1, DETAIL_COLS - 1);

    // Build workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');
    XLSX.utils.book_append_sheet(wb, detailWs, 'Daily Detail');

    const startStr = week ? week.startDate.format('YYYY-MM-DD') : 'start';
    const endStr = week ? week.endDate.format('YYYY-MM-DD') : 'end';
    const weekNum = week ? week.weekNumber : 'NA';
    const filename = `Weekly_Report_${campusCode}_Week_${weekNum}_${startStr}_to_${endStr}.xlsx`;

    XLSX.writeFile(wb, filename);
  };

  const handleExportAllCentersCurriculumReport = async () => {
    if (!isAdmin() || !initialLoadComplete || !selectedWeek) {
      return;
    }
    const attendanceForExport = await loadConsolidatedData();

    const week = weekOptions.find((w) => w.weekNumber === selectedWeek);
    if (!week) {
      return;
    }

    const weekStart = week.startDate;
    const weekEnd = week.endDate;
    const generatedOn = dayjs().format('DD MMM YYYY, hh:mm A');
    const dateRangeLine = `Date / تاریخ: ${weekStart.format('DD MMM YYYY')} – ${weekEnd.format('DD MMM YYYY')}`;
    const weekMetaLine = `Week No. ${week.weekNumber}  |  ${week.label}  |  ہفتہ نمبر ${week.weekNumber}`;

    const {
      titleStyle,
      subtitleStyle,
      generatedStyle,
      sectionHeadingStyle,
      tableHeaderStyle,
      cellBaseStyle,
      cellCenterStyle,
      altRowFill,
      setCell,
      ensureRange,
      xlsxTopicStyle,
      medium,
      allBorders
    } = createReportsThemeXlsx();

    const totalRowLabelStyle = {
      font: { bold: true, sz: 11, color: { rgb: '1B5E20' } },
      fill: { fgColor: { rgb: 'C8E6C9' } },
      alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
      border: allBorders(medium('1B5E20'))
    };
    const totalRowNumberStyle = {
      font: { bold: true, sz: 11 },
      fill: { fgColor: { rgb: 'C8E6C9' } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border: allBorders(medium('1B5E20'))
    };
    const grandTotalMergedStyle = {
      font: { bold: true, sz: 16, color: { rgb: '1B5E20' } },
      fill: { fgColor: { rgb: 'C8E6C9' } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border: allBorders(medium('1B5E20'))
    };

    const headerRowUrdu = [
      'مضمون',
      'تفویض کردہ کلاسز',
      'مرکز',
      'ہفتہ میں منعقدہ',
      'آج تک',
      'بقیہ',
      'آخری موضوع',
      'استاذ'
    ];
    const headerRowEn = [
      'Subject',
      'Assigned',
      'Center',
      'This week',
      'To date',
      'Remaining',
      'Last Topic',
      'Instructor'
    ];
    const campusHeaderUrdu = [
      'مضمون',
      'تفویض کردہ کلاسز',
      'ہفتہ میں منعقدہ',
      'آج تک',
      'بقیہ',
      'آخری موضوع',
      'استاذ'
    ];
    const campusHeaderEn = [
      'Subject',
      'Assigned',
      'This week',
      'To date',
      'Remaining',
      'Last Topic',
      'Instructor'
    ];

    const campusPool = campusesForConsolidatedReport(
      contextCampusesAllUnfiltered,
      contextCampuses
    );
    const clusters = buildConsolidatedCampusClusters(campusPool);

    const byClusterRows = new Map();
    for (const cluster of clusters) {
      const rows = computeCurriculumRowsForCluster(
        cluster,
        contextCourses,
        attendanceForExport,
        weekStart,
        weekEnd,
        instructorById
      );
      byClusterRows.set(cluster.clusterKey, { cluster, rows });
    }

    const courseToClusters = new Map();
    for (const { cluster, rows } of byClusterRows.values()) {
      for (const row of rows) {
        if (!courseToClusters.has(row.courseName)) {
          courseToClusters.set(row.courseName, []);
        }
        courseToClusters.get(row.courseName).push({ cluster, row });
      }
    }
    const sortedCourseNames = [...courseToClusters.keys()].sort((a, b) =>
      String(a).localeCompare(String(b))
    );
    for (const cn of sortedCourseNames) {
      courseToClusters.get(cn).sort((a, b) =>
        String(a.cluster.displayName).localeCompare(String(b.cluster.displayName))
      );
    }

    const writeSheetHeader = (ws, merges, colCount, titleText) => {
      setCell(ws, 'A1', titleText, titleStyle);
      for (let c = 1; c < colCount; c++) {
        setCell(ws, XLSX.utils.encode_cell({ r: 0, c }), '', titleStyle);
      }
      merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } });

      const mid = Math.ceil(colCount / 2);
      setCell(ws, 'A2', dateRangeLine, subtitleStyle);
      for (let c = 1; c < mid; c++) {
        setCell(ws, XLSX.utils.encode_cell({ r: 1, c }), '', subtitleStyle);
      }
      merges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: mid - 1 } });

      setCell(ws, XLSX.utils.encode_cell({ r: 1, c: mid }), `Generated: ${generatedOn}`, generatedStyle);
      for (let c = mid + 1; c < colCount; c++) {
        setCell(ws, XLSX.utils.encode_cell({ r: 1, c }), '', generatedStyle);
      }
      merges.push({ s: { r: 1, c: mid }, e: { r: 1, c: colCount - 1 } });

      setCell(ws, 'A3', weekMetaLine, subtitleStyle);
      for (let c = 1; c < colCount; c++) {
        setCell(ws, XLSX.utils.encode_cell({ r: 2, c }), '', subtitleStyle);
      }
      merges.push({ s: { r: 2, c: 0 }, e: { r: 2, c: colCount - 1 } });
    };

    const MAIN_COLS = 8;
    const mainWs = {};
    const mainMerges = [];
    writeSheetHeader(
      mainWs,
      mainMerges,
      MAIN_COLS,
      'ہفتہ وار تکمیلِ نصاب / 2026-27 consolidated (all campuses)'
    );

    const mainHeaderBilingual = headerRowUrdu.map(
      (ur, i) => `${ur}\n${headerRowEn[i]}`
    );
    let mainRow = 3;
    mainHeaderBilingual.forEach((h, i) => {
      setCell(mainWs, XLSX.utils.encode_cell({ r: mainRow, c: i }), h, tableHeaderStyle);
    });
    mainRow += 1;

    let mainBodyIdx = 0;
    for (const courseName of sortedCourseNames) {
      const list = courseToClusters.get(courseName);
      list.forEach((item, idx) => {
        const { cluster, row } = item;
        const isFirst = idx === 0;
        const isAlt = mainBodyIdx % 2 === 1;
        const baseFill = isAlt ? altRowFill : { fgColor: { rgb: 'FFFFFF' } };
        const left = { ...cellBaseStyle, fill: baseFill };
        const center = { ...cellCenterStyle, fill: baseFill };
        const topicStr = row.weekTopic || '';
        const subjectCellStyle =
          isFirst && row.courseName
            ? consolidatedCourseNameCellStyle(left, row.courseName)
            : left;

        setCell(
          mainWs,
          XLSX.utils.encode_cell({ r: mainRow, c: 0 }),
          isFirst ? row.courseName : '',
          subjectCellStyle
        );
        setCell(
          mainWs,
          XLSX.utils.encode_cell({ r: mainRow, c: 1 }),
          isFirst ? row.assigned : '',
          center
        );
        setCell(
          mainWs,
          XLSX.utils.encode_cell({ r: mainRow, c: 2 }),
          cluster.displayName,
          left
        );
        setCell(mainWs, XLSX.utils.encode_cell({ r: mainRow, c: 3 }), row.weekCount, center);
        setCell(mainWs, XLSX.utils.encode_cell({ r: mainRow, c: 4 }), row.toDate, center);
        setCell(mainWs, XLSX.utils.encode_cell({ r: mainRow, c: 5 }), row.remaining, center);
        setCell(
          mainWs,
          XLSX.utils.encode_cell({ r: mainRow, c: 6 }),
          topicStr,
          xlsxTopicStyle(left, topicStr)
        );
        setCell(mainWs, XLSX.utils.encode_cell({ r: mainRow, c: 7 }), row.instructor, left);
        mainRow += 1;
        mainBodyIdx += 1;
      });
    }

    mainRow += 1;
    setCell(
      mainWs,
      XLSX.utils.encode_cell({ r: mainRow, c: 0 }),
      'میزان و ضائع (مراکز) / Center totals / shortfalls',
      sectionHeadingStyle
    );
    for (let c = 1; c < MAIN_COLS; c++) {
      setCell(mainWs, XLSX.utils.encode_cell({ r: mainRow, c }), '', sectionHeadingStyle);
    }
    mainMerges.push({
      s: { r: mainRow, c: 0 },
      e: { r: mainRow, c: MAIN_COLS - 1 }
    });
    mainRow += 1;

    const perCampusClassTotal = CONSOLIDATED_PER_CAMPUS_CLASS_TOTAL;
    const planWeeks = weekOptions.length || 52;
    const weeksLeftInPlan = Math.max(1, planWeeks - week.weekNumber + 1);

    const campusSummaryRows = [...byClusterRows.values()]
      .map(({ cluster, rows }) => {
        let weekSum = 0;
        let toDateSum = 0;
        for (const row of rows) {
          weekSum += Number(row.weekCount) || 0;
          toDateSum += Number(row.toDate) || 0;
        }
        const remainingClasses = Math.max(0, perCampusClassTotal - toDateSum);
        const weeklyWastedShortfall = Math.max(
          0,
          CONSOLIDATED_CLASSES_PER_WEEK - weekSum
        );
        const idealCumulativeBy19Pace = Math.min(
          week.weekNumber * CONSOLIDATED_CLASSES_PER_WEEK,
          perCampusClassTotal
        );
        const cumulativeShortfallCarryForward = Math.max(
          0,
          idealCumulativeBy19Pace - toDateSum
        );
        return {
          cluster,
          weekSum,
          toDateSum,
          remainingClasses,
          weeklyWastedShortfall
        };
      })
      .sort((a, b) =>
        String(a.cluster.displayName).localeCompare(String(b.cluster.displayName))
      );

    const summaryHdrUrdu = [
      'مرکز',
      'ہفتہ',
      'آج تک',
      'بقیہ',
      'ضائع'
    ];
    const summaryHdrEn = [
      'Center',
      'This week',
      'To date',
      'Remaining',
      'Shortfall'
    ];
    const summaryHeaderBilingual = summaryHdrUrdu.map(
      (ur, i) => `${ur}\n${summaryHdrEn[i]}`
    );

    summaryHeaderBilingual.forEach((h, i) => {
      setCell(mainWs, XLSX.utils.encode_cell({ r: mainRow, c: i }), h, tableHeaderStyle);
    });
    setCell(
      mainWs,
      XLSX.utils.encode_cell({ r: mainRow, c: 5 }),
      'کل میزان\nGrand total',
      tableHeaderStyle
    );
    setCell(mainWs, XLSX.utils.encode_cell({ r: mainRow, c: 6 }), '', tableHeaderStyle);
    setCell(mainWs, XLSX.utils.encode_cell({ r: mainRow, c: 7 }), '', tableHeaderStyle);
    mainMerges.push({
      s: { r: mainRow, c: 5 },
      e: { r: mainRow, c: 7 }
    });
    mainRow += 1;

    const summaryFirstDataRow = mainRow;
    campusSummaryRows.forEach((rec, i) => {
      const fillRgb = CONSOLIDATED_CAMPUS_SUMMARY_ROW_FILLS[
        i % CONSOLIDATED_CAMPUS_SUMMARY_ROW_FILLS.length
      ];
      const baseFill = { fgColor: { rgb: fillRgb } };
      const left = { ...cellBaseStyle, fill: baseFill };
      const center = { ...cellCenterStyle, fill: baseFill };
      const nameStr = rec.cluster.displayName || '';
      setCell(
        mainWs,
        XLSX.utils.encode_cell({ r: mainRow, c: 0 }),
        nameStr,
        xlsxTopicStyle(left, nameStr)
      );
      setCell(mainWs, XLSX.utils.encode_cell({ r: mainRow, c: 1 }), rec.weekSum, center);
      setCell(mainWs, XLSX.utils.encode_cell({ r: mainRow, c: 2 }), rec.toDateSum, center);
      setCell(
        mainWs,
        XLSX.utils.encode_cell({ r: mainRow, c: 3 }),
        rec.remainingClasses,
        center
      );
      setCell(
        mainWs,
        XLSX.utils.encode_cell({ r: mainRow, c: 4 }),
        rec.weeklyWastedShortfall,
        center
      );
      setCell(
        mainWs,
        XLSX.utils.encode_cell({ r: mainRow, c: 5 }),
        '',
        center
      );
      setCell(mainWs, XLSX.utils.encode_cell({ r: mainRow, c: 6 }), '', center);
      setCell(mainWs, XLSX.utils.encode_cell({ r: mainRow, c: 7 }), '', center);
      mainRow += 1;
    });
    const summaryLastDataRow = mainRow - 1;

    if (campusSummaryRows.length > 0) {
      // Pre-style whole merged grand-total block so borders/fill render correctly.
      for (let rr = summaryFirstDataRow; rr <= summaryLastDataRow; rr += 1) {
        for (let cc = 5; cc <= 7; cc += 1) {
          setCell(
            mainWs,
            XLSX.utils.encode_cell({ r: rr, c: cc }),
            '',
            grandTotalMergedStyle
          );
        }
      }
      mainMerges.push({
        s: { r: summaryFirstDataRow, c: 5 },
        e: { r: summaryLastDataRow, c: 7 }
      });
      const gtAddr = XLSX.utils.encode_cell({ r: summaryFirstDataRow, c: 5 });
      const kulStr = `کل میزان:\n${perCampusClassTotal}`;
      setCell(mainWs, gtAddr, kulStr, xlsxTopicStyle(grandTotalMergedStyle, kulStr));
    } else {
      setCell(
        mainWs,
        XLSX.utils.encode_cell({ r: mainRow, c: 0 }),
        'کوئی مرکز نہیں / No campus rows',
        { ...cellBaseStyle, font: { ...cellBaseStyle.font, italic: true } }
      );
      for (let c = 1; c < MAIN_COLS; c++) {
        setCell(mainWs, XLSX.utils.encode_cell({ r: mainRow, c }), '', cellBaseStyle);
      }
      mainMerges.push({
        s: { r: mainRow, c: 0 },
        e: { r: mainRow, c: MAIN_COLS - 1 }
      });
      mainRow += 1;
    }

    mainWs['!cols'] = [
      { wch: 26 },
      { wch: 12 },
      { wch: 22 },
      { wch: 11 },
      { wch: 12 },
      { wch: 10 },
      { wch: 40 },
      { wch: 24 }
    ];
    mainWs['!merges'] = mainMerges;
    mainWs['!rows'] = [{ hpt: 32 }, { hpt: 26 }, { hpt: 24 }, { hpt: 44 }];
    ensureRange(mainWs, Math.max(mainRow - 1, 4), MAIN_COLS - 1);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, mainWs, 'Main');

    const CAMPUS_COLS = 7;
    const usedSheetNames = new Set(['Main']);
    for (const { cluster, rows } of byClusterRows.values()) {
      const sheetName = safeExcelSheetName(cluster.displayName, usedSheetNames);
      const codesLabel =
        cluster.codes.length > 1
          ? `codes: ${cluster.codes.join(', ')}`
          : cluster.codes[0] || '';
      const ws = {};
      const merges = [];
      writeSheetHeader(
        ws,
        merges,
        CAMPUS_COLS,
        `ہفتہ وار تکمیلِ نصاب / ${cluster.displayName}${codesLabel ? ` (${codesLabel})` : ''}`
      );

      const campusHeaderBilingual = campusHeaderUrdu.map(
        (ur, i) => `${ur}\n${campusHeaderEn[i]}`
      );
      let r = 3;
      campusHeaderBilingual.forEach((h, i) => {
        setCell(ws, XLSX.utils.encode_cell({ r, c: i }), h, tableHeaderStyle);
      });
      r += 1;

      let cSumA = 0;
      let cSumW = 0;
      let cSumT = 0;
      let cSumR = 0;

      rows.forEach((row, idx) => {
        const isAlt = idx % 2 === 1;
        const baseFill = isAlt ? altRowFill : { fgColor: { rgb: 'FFFFFF' } };
        const left = { ...cellBaseStyle, fill: baseFill };
        const center = { ...cellCenterStyle, fill: baseFill };
        const topicStr = row.weekTopic || '';

        cSumA += Number(row.assigned) || 0;
        cSumW += Number(row.weekCount) || 0;
        cSumT += Number(row.toDate) || 0;
        cSumR += Number(row.remaining) || 0;

        const courseNameStyle = row.courseName
          ? consolidatedCourseNameCellStyle(left, row.courseName)
          : left;
        setCell(ws, XLSX.utils.encode_cell({ r, c: 0 }), row.courseName, courseNameStyle);
        setCell(ws, XLSX.utils.encode_cell({ r, c: 1 }), row.assigned, center);
        setCell(ws, XLSX.utils.encode_cell({ r, c: 2 }), row.weekCount, center);
        setCell(ws, XLSX.utils.encode_cell({ r, c: 3 }), row.toDate, center);
        setCell(ws, XLSX.utils.encode_cell({ r, c: 4 }), row.remaining, center);
        setCell(
          ws,
          XLSX.utils.encode_cell({ r, c: 5 }),
          topicStr,
          xlsxTopicStyle(left, topicStr)
        );
        setCell(ws, XLSX.utils.encode_cell({ r, c: 6 }), row.instructor, left);
        r += 1;
      });

      r += 1;
      setCell(ws, XLSX.utils.encode_cell({ r, c: 0 }), 'Subtotal / ذیلی کل', totalRowLabelStyle);
      setCell(ws, XLSX.utils.encode_cell({ r, c: 1 }), cSumA, totalRowNumberStyle);
      setCell(ws, XLSX.utils.encode_cell({ r, c: 2 }), cSumW, totalRowNumberStyle);
      setCell(ws, XLSX.utils.encode_cell({ r, c: 3 }), cSumT, totalRowNumberStyle);
      setCell(ws, XLSX.utils.encode_cell({ r, c: 4 }), cSumR, totalRowNumberStyle);
      setCell(ws, XLSX.utils.encode_cell({ r, c: 5 }), '', totalRowLabelStyle);
      setCell(ws, XLSX.utils.encode_cell({ r, c: 6 }), '', totalRowLabelStyle);
      r += 1;

      ws['!cols'] = [
        { wch: 28 },
        { wch: 12 },
        { wch: 11 },
        { wch: 12 },
        { wch: 10 },
        { wch: 42 },
        { wch: 24 }
      ];
      ws['!merges'] = merges;
      ws['!rows'] = [{ hpt: 32 }, { hpt: 26 }, { hpt: 24 }, { hpt: 44 }];
      ensureRange(ws, Math.max(r - 1, 4), CAMPUS_COLS - 1);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    }

    const startStr = weekStart.format('YYYY-MM-DD');
    const endStr = weekEnd.format('YYYY-MM-DD');
    const filename = `DW-RIQC_2026-27_Consolidated_Week_${week.weekNumber}_${startStr}_to_${endStr}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  // Export Course-Wise Attendance Report as styled XLSX
  const handleExportCourseWiseAttendance = () => {
    if (!selectedCourseForReport) {
      return;
    }

    const filteredAttendance = contextAttendance.filter(att => {
      const matchesCourse = att.courseName === selectedCourseForReport;
      const matchesCampus = att.campusCode === selectedCampus;
      return matchesCourse && matchesCampus;
    });

    if (filteredAttendance.length === 0) {
      return;
    }

    const campus = contextCampuses.find(c => c.campusCode === selectedCampus);
    const campusName = campus?.campusName || 'Unknown Campus';
    const campusCode = campus?.campusCode || selectedCampus || 'N/A';
    const generatedOn = dayjs().format('DD MMM YYYY, hh:mm A');

    const attendanceRecords = filteredAttendance.map(att => {
      const start = dayjs(att.startTime?.replace(/-(\d{1,2}):(\d{2})$/, ' $1:$2'), 'YYYY-MM-DD HH:mm');
      const { presentCount, absentCount, leaveCount } = dedupeAttendanceBuckets(att);
      const totalStudents = presentCount + absentCount + leaveCount;
      const pct = sessionPresentPct(
        presentCount + leaveCount,
        campusActiveStudentCount
      );

      return {
        dateTime: start.isValid() ? start.format('DD/MM/YYYY - hh:mm A') : att.startTime || 'N/A',
        present: presentCount,
        absent: absentCount,
        leaves: leaveCount,
        total: totalStudents,
        attendancePctNum: pct,
        attendancePct: pct === null || isNaN(pct) ? '—' : `${pct.toFixed(1)}%`,
        topic: att.topic || '-',
        comments: att.comments || '-',
        instructor: getInstructorNameForCourseName(att.courseName, contextCourses, instructorById)
      };
    }).sort((a, b) => {
      const dateA = dayjs(a.dateTime.split(' - ')[0], 'DD/MM/YYYY');
      const dateB = dayjs(b.dateTime.split(' - ')[0], 'DD/MM/YYYY');
      return dateB - dateA;
    });

    const {
      titleStyle,
      subtitleStyle,
      generatedStyle,
      sectionHeadingStyle,
      tableHeaderStyle,
      cellBaseStyle,
      cellCenterStyle,
      altRowFill,
      attendanceCellStyle,
      setCell,
      ensureRange,
      xlsxTopicStyle
    } = createReportsThemeXlsx();

    const ws = {};
    const merges = [];
    const COLS = 9;

    setCell(ws, 'A1', `Course-Wise Attendance — ${selectedCourseForReport}`, titleStyle);
    for (let c = 1; c < COLS; c++) {
      setCell(ws, XLSX.utils.encode_cell({ r: 0, c }), '', titleStyle);
    }
    merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: COLS - 1 } });

    setCell(ws, 'A2', `${campusName} (${campusCode})`, subtitleStyle);
    for (let c = 1; c < 5; c++) {
      setCell(ws, XLSX.utils.encode_cell({ r: 1, c }), '', subtitleStyle);
    }
    merges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: 4 } });
    setCell(ws, XLSX.utils.encode_cell({ r: 1, c: 5 }), `Generated: ${generatedOn}`, generatedStyle);
    for (let c = 6; c < COLS; c++) {
      setCell(ws, XLSX.utils.encode_cell({ r: 1, c }), '', generatedStyle);
    }
    merges.push({ s: { r: 1, c: 5 }, e: { r: 1, c: COLS - 1 } });

    let r = 3;
    setCell(ws, XLSX.utils.encode_cell({ r, c: 0 }), 'Session log', sectionHeadingStyle);
    r += 1;

    const headers = [
      'Date & Time',
      'Present',
      'Absent',
      'Leaves',
      'Total',
      'Attendance %',
      'Topic',
      'Comments',
      'Instructor'
    ];
    headers.forEach((h, c) => {
      setCell(ws, XLSX.utils.encode_cell({ r, c }), h, tableHeaderStyle);
    });
    r += 1;

    attendanceRecords.forEach((record, idx) => {
      const isAlt = idx % 2 === 1;
      const baseFill = isAlt ? altRowFill : { fgColor: { rgb: 'FFFFFF' } };
      const left = { ...cellBaseStyle, fill: baseFill };
      const center = { ...cellCenterStyle, fill: baseFill };

      setCell(ws, XLSX.utils.encode_cell({ r, c: 0 }), record.dateTime, left);
      setCell(ws, XLSX.utils.encode_cell({ r, c: 1 }), record.present, center);
      setCell(ws, XLSX.utils.encode_cell({ r, c: 2 }), record.absent, center);
      setCell(ws, XLSX.utils.encode_cell({ r, c: 3 }), record.leaves, center);
      setCell(ws, XLSX.utils.encode_cell({ r, c: 4 }), record.total, center);
      setCell(
        ws,
        XLSX.utils.encode_cell({ r, c: 5 }),
        record.attendancePct,
        attendanceCellStyle(record.attendancePctNum)
      );
      setCell(ws, XLSX.utils.encode_cell({ r, c: 6 }), record.topic, xlsxTopicStyle(left, record.topic));
      setCell(ws, XLSX.utils.encode_cell({ r, c: 7 }), record.comments, xlsxTopicStyle(left, record.comments));
      setCell(ws, XLSX.utils.encode_cell({ r, c: 8 }), record.instructor, left);
      r += 1;
    });

    ws['!cols'] = [
      { wch: 22 },
      { wch: 10 },
      { wch: 10 },
      { wch: 10 },
      { wch: 10 },
      { wch: 14 },
      { wch: 36 },
      { wch: 28 },
      { wch: 22 }
    ];
    ws['!merges'] = merges;
    ws['!rows'] = [{ hpt: 32 }, { hpt: 22 }];
    ensureRange(ws, r - 1, COLS - 1);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Attendance');
    const courseNameForFile = selectedCourseForReport.replace(/\s+/g, '_');
    const filename = `Course_Wise_Attendance_${courseNameForFile}_${campusCode}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  // Export Total Classes Report as styled XLSX
  const handleExportTotalClassesReport = () => {
    if (!courseTableData || courseTableData.length === 0) {
      return;
    }

    const campus = contextCampuses.find(c => c.campusCode === selectedCampus);
    const campusName = campus?.campusName || 'Unknown Campus';
    const campusCode = campus?.campusCode || selectedCampus || 'N/A';
    const generatedOn = dayjs().format('DD MMM YYYY, hh:mm A');
    const summary = calculateSummary();

    const {
      titleStyle,
      subtitleStyle,
      generatedStyle,
      sectionHeadingStyle,
      kpiLabelStyle,
      kpiValueStyle,
      tableHeaderStyle,
      cellBaseStyle,
      cellCenterStyle,
      altRowFill,
      setCell,
      ensureRange,
      xlsxTopicStyle,
      medium,
      allBorders
    } = createReportsThemeXlsx();

    const ws = {};
    const merges = [];
    const COLS = 6;

    setCell(ws, 'A1', `Total Classes Report — ${campusName} (${campusCode})`, titleStyle);
    for (let c = 1; c < COLS; c++) {
      setCell(ws, XLSX.utils.encode_cell({ r: 0, c }), '', titleStyle);
    }
    merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: COLS - 1 } });

    setCell(ws, 'A2', 'Planned vs conducted classes and last topic', subtitleStyle);
    for (let c = 1; c < 4; c++) {
      setCell(ws, XLSX.utils.encode_cell({ r: 1, c }), '', subtitleStyle);
    }
    merges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: 3 } });
    setCell(ws, 'E2', `Generated: ${generatedOn}`, generatedStyle);
    setCell(ws, 'F2', '', generatedStyle);
    merges.push({ s: { r: 1, c: 4 }, e: { r: 1, c: 5 } });

    let row = 3;
    setCell(ws, XLSX.utils.encode_cell({ r: row, c: 0 }), 'Overview', sectionHeadingStyle);
    row += 1;

    const kpis = [
      { label: 'Total planned (all courses)', value: summary.totalClasses },
      { label: 'Classes conducted', value: summary.totalConducted },
      { label: 'Remaining', value: summary.totalRemaining }
    ];
    kpis.forEach((kpi, i) => {
      const startCol = i * 2;
      setCell(ws, XLSX.utils.encode_cell({ r: row, c: startCol }), kpi.label, kpiLabelStyle);
      setCell(ws, XLSX.utils.encode_cell({ r: row, c: startCol + 1 }), '', kpiLabelStyle);
      merges.push({ s: { r: row, c: startCol }, e: { r: row, c: startCol + 1 } });
      setCell(ws, XLSX.utils.encode_cell({ r: row + 1, c: startCol }), kpi.value, kpiValueStyle);
      setCell(ws, XLSX.utils.encode_cell({ r: row + 1, c: startCol + 1 }), '', kpiValueStyle);
      merges.push({ s: { r: row + 1, c: startCol }, e: { r: row + 1, c: startCol + 1 } });
    });
    row += 3;

    const headers = [
      'Course Name',
      'Total Classes',
      'Conducted',
      'Remaining',
      'Teacher',
      'Last Topic'
    ];
    headers.forEach((h, c) => {
      setCell(ws, XLSX.utils.encode_cell({ r: row, c }), h, tableHeaderStyle);
    });
    row += 1;

    courseTableData.forEach((dataRow, idx) => {
      const isAlt = idx % 2 === 1;
      const baseFill = isAlt ? altRowFill : { fgColor: { rgb: 'FFFFFF' } };
      const left = { ...cellBaseStyle, fill: baseFill };
      const center = { ...cellCenterStyle, fill: baseFill };
      const lastTopic = dataRow.lastTopic || 'N/A';

      setCell(ws, XLSX.utils.encode_cell({ r: row, c: 0 }), dataRow.courseName, left);
      setCell(ws, XLSX.utils.encode_cell({ r: row, c: 1 }), dataRow.totalClasses, center);
      setCell(ws, XLSX.utils.encode_cell({ r: row, c: 2 }), dataRow.totalClassesConducted, center);
      setCell(ws, XLSX.utils.encode_cell({ r: row, c: 3 }), dataRow.remainingClasses, center);
      setCell(ws, XLSX.utils.encode_cell({ r: row, c: 4 }), dataRow.instructor, left);
      setCell(
        ws,
        XLSX.utils.encode_cell({ r: row, c: 5 }),
        lastTopic,
        xlsxTopicStyle(left, lastTopic)
      );
      row += 1;
    });

    const totalRowStyle = {
      font: { bold: true, sz: 11 },
      fill: { fgColor: { rgb: 'C8E6C9' } },
      border: allBorders(medium('1B5E20')),
      alignment: { horizontal: 'left', vertical: 'center', wrapText: true }
    };
    const totalCenterStyle = {
      ...totalRowStyle,
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true }
    };

    setCell(ws, XLSX.utils.encode_cell({ r: row, c: 0 }), 'TOTAL', totalRowStyle);
    setCell(ws, XLSX.utils.encode_cell({ r: row, c: 1 }), summary.totalClasses, totalCenterStyle);
    setCell(ws, XLSX.utils.encode_cell({ r: row, c: 2 }), summary.totalConducted, totalCenterStyle);
    setCell(ws, XLSX.utils.encode_cell({ r: row, c: 3 }), summary.totalRemaining, totalCenterStyle);
    setCell(ws, XLSX.utils.encode_cell({ r: row, c: 4 }), '', totalRowStyle);
    setCell(ws, XLSX.utils.encode_cell({ r: row, c: 5 }), '', totalRowStyle);

    ws['!cols'] = [
      { wch: 28 },
      { wch: 14 },
      { wch: 12 },
      { wch: 12 },
      { wch: 22 },
      { wch: 40 }
    ];
    ws['!merges'] = merges;
    ws['!rows'] = [{ hpt: 32 }, { hpt: 22 }];
    ensureRange(ws, row, COLS - 1);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Total Classes');
    const filename = `Total_Classes_Report_${campusCode}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  // Group classes by date for better visualization
  const groupClassesByDate = () => {
    const grouped = {};
    
    attendanceTableData.forEach(row => {
      const date = row.date;
      if (!grouped[date]) {
        const dayName = row.test.format('dddd').toLowerCase();
        grouped[date] = {
          date: date,
          dayOfWeek: row.test.format('dddd'),
          dayName: dayName,
          classes: []
        };
      }
      grouped[date].classes.push(row);
    });
    
    // Convert to array and sort by date
    return Object.values(grouped).sort((a, b) => {
      const dateA = dayjs(a.date, 'DD/MM/YYYY');
      const dateB = dayjs(b.date, 'DD/MM/YYYY');
      return dateA - dateB;
    });
  };

  // Per-subject summary: all campus courses for the week (including 0 classes this week)
  const weeklySubjectSummary = useMemo(() => {
    if (!selectedWeek || !selectedCampus) return [];

    const week = weekOptions.find((w) => w.weekNumber === selectedWeek);
    if (!week) return [];

    const campusForAnchor = contextCampuses.find(
      (c) => c.campusCode === selectedCampus
    );
    const batchAnchor = getReportWeekStartAnchorForCampus(campusForAnchor);

    return buildWeeklySubjectSummaryRows({
      campusCode: selectedCampus,
      contextCourses,
      contextAttendance,
      attendanceRowsThisWeek: attendanceTableData,
      coursePlanByName,
      instructorById,
      campusActiveStudentCount,
      batchAnchor,
      weekEnd: week.endDate
    });
  }, [
    selectedWeek,
    selectedCampus,
    attendanceTableData,
    campusActiveStudentCount,
    coursePlanByName,
    weekOptions,
    contextAttendance,
    contextCampuses,
    contextCourses,
    instructorById
  ]);

  const weeklyHeaderStats = useMemo(() => {
    if (!selectedWeek) {
      return {
        totalClasses: 0,
        campusCourses: 0,
        coursesWithActivity: 0
      };
    }
    return {
      totalClasses: attendanceTableData.length,
      campusCourses: weeklySubjectSummary.length,
      coursesWithActivity: new Set(attendanceTableData.map((r) => r.courseName))
        .size
    };
  }, [selectedWeek, attendanceTableData, weeklySubjectSummary]);

  const summary = calculateSummary();
  const groupedClasses = groupClassesByDate();


  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleCampusChange = (event) => {
    setSelectedCampus(event.target.value);
  };

  // Auto-select campus if only one is available
  useEffect(() => {
    if (initialLoadComplete && contextCampuses.length === 1 && !selectedCampus) {
      setSelectedCampus(contextCampuses[0].campusCode);
    }
  }, [initialLoadComplete, contextCampuses, selectedCampus]);

  return (
      <>
        <Box mt={4}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, color: '#2E7D32' }}>
            📊 Reports Dashboard
          </Typography>

          {/* Campus Selection */}
          <Paper elevation={2} sx={{ mb: 3, p: 2, borderRadius: 2 }}>
            <FormControl sx={{ minWidth: 300, maxWidth: 400 }}>
              <InputLabel sx={{ color: '#4CAF50' }}>Select Campus</InputLabel>
              <Select
                value={selectedCampus}
                onChange={handleCampusChange}
                input={<OutlinedInput label="Select Campus" />}
                disabled={contextCampuses.length === 1}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#4CAF50',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2E7D32',
                    },
                  },
                }}
              >
                {contextCampuses.map((campus) => (
                  <MenuItem key={campus.id} value={campus.campusCode}>
                    {campus.campusName} ({campus.campusCode})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Paper>

          {/* Tabs Menu */}
          <Paper elevation={2} sx={{ mb: 3 }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  minHeight: 64
                },
                '& .Mui-selected': {
                  color: '#2E7D32'
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#2E7D32',
                  height: 3
                }
              }}
            >
              <Tab
                icon={<HubOutlinedIcon />}
                iconPosition="start"
                label="Consolidated Report"
              />
              <Tab 
                icon={<CalendarTodayIcon />} 
                iconPosition="start" 
                label="Weekly Report" 
              />
              <Tab 
                icon={<AssessmentIcon />} 
                iconPosition="start" 
                label="Total Classes Report" 
              />
              <Tab 
                icon={<CalendarTodayIcon />} 
                iconPosition="start" 
                label="Course-Wise Attendance" 
              />
            </Tabs>
          </Paper>
        </Box>

        {/* Consolidated (all centers) Report — Admin export */}
        {activeTab === 0 && (
        <Box
          mt={4}
          sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, maxWidth: 640 }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.5rem' }}>
            Consolidated report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            2026-27 campuses only (all loaded from system). Same name → one row.
          </Typography>

          {!isAdmin() && (
            <Alert severity="warning" sx={{ py: 0.5 }}>
              Admin only: download.
            </Alert>
          )}

          <FormControl sx={{ minWidth: 280 }} variant="outlined" size="small">
            <InputLabel>Week</InputLabel>
            <Select
              value={selectedWeek}
              onChange={handleWeekChange}
              label="Week"
            >
              {weekOptions.map((w) => (
                <MenuItem key={w.weekNumber} value={w.weekNumber}>
                  Week {w.weekNumber}: {w.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              {consolidatedCampusClustersPreview.length} center
              {consolidatedCampusClustersPreview.length !== 1 ? 's' : ''} ·{' '}
              {consolidatedCampusPool.length} code{consolidatedCampusPool.length !== 1 ? 's' : ''}{' '}
              (2026-27)
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.4 }}>
              {consolidatedCampusClustersPreview.length === 0
                ? 'No 2026-27 campuses match (check name/code contains 2026-27 or 2026_27).'
                : consolidatedCampusClustersPreview
                    .map(
                      (cl) =>
                        `${cl.displayName}${
                          cl.codes.length > 1 ? ` (${cl.codes.length})` : ''
                        }`
                    )
                    .join(' · ')}
            </Typography>
          </Paper>

          {isAdmin() && (
            <Button
              variant="contained"
              size="medium"
              disabled={
                !selectedWeek ||
                !initialLoadComplete ||
                loadingConsolidatedData ||
                consolidatedCampusClustersPreview.length === 0
              }
              onClick={handleExportAllCentersCurriculumReport}
              startIcon={<DownloadIcon />}
              sx={{
                alignSelf: 'flex-start',
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: '#1565C0',
                '&:hover': { bgcolor: '#0D47A1' }
              }}
            >
              {loadingConsolidatedData ? 'Loading consolidated data...' : 'Generate consolidated Excel'}
            </Button>
          )}
        </Box>
        )}

        {/* Weekly Report Tab */}
        {activeTab === 1 && (
        <Box mt={4}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
              flexWrap: 'wrap',
              gap: 1
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ mb: 0, fontWeight: 600, fontSize: '1.5rem' }}>
              Weekly Report
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              {selectedWeek &&
                selectedCampus &&
                (weeklySubjectSummary.length > 0 ||
                  attendanceTableData.length > 0) && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleExportWeeklyReport}
                  startIcon={<DownloadIcon />}
                  sx={{
                    borderColor: '#4CAF50',
                    color: '#4CAF50',
                    '&:hover': { borderColor: '#45a049', bgcolor: '#e8f5e9' },
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  Download Report
                </Button>
              )}
            </Box>
          </Box>

          <FormControl sx={{ minWidth: 200 }} variant="outlined" margin="normal">
            <InputLabel>Select a week</InputLabel>
            <Select
                value={selectedWeek}
                onChange={handleWeekChange}
                label="Select a week"
            >
              {weekOptions.map(w => (
                  <MenuItem key={w.weekNumber} value={w.weekNumber}>
                    Week {w.weekNumber}: {w.label}
                  </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Weekly Summary Cards */}
          {selectedWeek && (
            <Grid container spacing={3} sx={{ mb: 3, mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Card 
                  elevation={3}
                  sx={{ 
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                    color: 'white',
                    borderRadius: 3,
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 12px 20px rgba(79, 172, 254, 0.4)'
                    }
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 500, opacity: 0.9 }}>
                      📅 Classes This Week
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700 }}>
                      {weeklyHeaderStats.totalClasses}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                      Week {selectedWeek}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card 
                  elevation={3}
                  sx={{ 
                    background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                    color: 'white',
                    borderRadius: 3,
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 12px 20px rgba(250, 112, 154, 0.4)'
                    }
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 500, opacity: 0.9 }}>
                      📚 Campus Courses
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700 }}>
                      {weeklyHeaderStats.campusCourses}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                      With class this week: {weeklyHeaderStats.coursesWithActivity}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Subject Summary Table */}
          {selectedWeek && weeklySubjectSummary.length > 0 && (
            <Paper
              elevation={2}
              sx={{
                mb: 3,
                borderRadius: 3,
                overflow: 'hidden',
                border: '1px solid rgba(76, 175, 80, 0.25)'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 3,
                  py: 2,
                  background: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 100%)',
                  color: 'white'
                }}
              >
                <AssessmentIcon />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    Subject Summary
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.85 }}>
                    Per-subject overview for the selected week. Last topic prefers this week and
                    otherwise shows the most recent previous topic up to this week. To date counts
                    sessions from batch Week 1 through the end of this week (not all-time). Remaining
                    = planned minus to date.
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }} />
                <Chip
                  label={`${weeklySubjectSummary.length} ${weeklySubjectSummary.length === 1 ? 'subject' : 'subjects'}`}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 600
                  }}
                />
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#e8f5e9' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#2E7D32' }}>Subject</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#2E7D32' }}>
                        Instructor
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: '#2E7D32' }}>
                        This week
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: '#2E7D32' }}>
                        Planned
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: '#2E7D32' }}>
                        To date
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: '#2E7D32' }}>
                        Remaining
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#2E7D32' }}>
                        Last Topic
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: '#2E7D32' }}>
                        Avg. Attendance
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {weeklySubjectSummary.map((row, idx) => {
                      const pct = row.avgAttendancePct;
                      let chipColor = 'default';
                      let chipLabel = '—';
                      if (pct !== null && !isNaN(pct)) {
                        chipLabel = `${pct.toFixed(1)}%`;
                        if (pct > ATTENDANCE_PCT_GOOD_ABOVE) chipColor = 'success';
                        else if (pct >= ATTENDANCE_PCT_WARN_MIN) chipColor = 'warning';
                        else chipColor = 'error';
                      }
                      return (
                        <TableRow
                          key={row.subject + idx}
                          sx={{
                            '&:hover': { bgcolor: '#f5f5f5' },
                            '&:nth-of-type(even)': { bgcolor: '#fafafa' }
                          }}
                        >
                          <TableCell>
                            <Chip
                              label={row.subject}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontWeight: 500 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {row.instructor || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {row.classes}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {row.plannedTotal !== null ? row.plannedTotal : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {row.conductedThroughWeekEnd}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color:
                                  row.remainingClasses !== null && row.remainingClasses === 0
                                    ? 'success.main'
                                    : 'text.primary'
                              }}
                            >
                              {row.remainingClasses !== null ? row.remainingClasses : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              className={
                                isUrduText(row.lastTopic || '') ? 'urdu' : ''
                              }
                              sx={{
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                ...getUrduSxProps(row.lastTopic || '')
                              }}
                            >
                              {row.lastTopic || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={chipLabel}
                              size="small"
                              color={chipColor}
                              sx={{ fontWeight: 600, minWidth: 64 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Grouped Classes by Date */}
          {selectedWeek ? (
            <Box sx={{ mt: 3 }}>
              {groupedClasses.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary">
                    No classes found for this week
                  </Typography>
                </Paper>
              ) : (
                groupedClasses.map((dayGroup, index) => (
                  <Accordion 
                    key={index} 
                    defaultExpanded={index === 0}
                    sx={{ 
                      mb: 2,
                      borderRadius: 2,
                      '&:before': { display: 'none' },
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        bgcolor: '#f5f5f5',
                        borderRadius: 2,
                        '&:hover': {
                          bgcolor: '#eeeeee'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#2E7D32' }}>
                          📅 {dayGroup.dayOfWeek}
                        </Typography>
                        <Chip 
                          label={dayGroup.date} 
                          color="primary" 
                          size="small"
                        />
                        <Chip 
                          label={`${dayGroup.classes.length} ${dayGroup.classes.length === 1 ? 'Class' : 'Classes'}`}
                          color="success"
                          size="small"
                        />
                        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent accordion from expanding/collapsing
                              handleModify(dayGroup.date);
                            }}
                            sx={{
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 500
                            }}
                          >
                            Modify
                          </Button>
                        </Box>
        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      {/* Warning if more records than allowed slots */}
                      {(() => {
                        const maxSlots = SLOTS[dayGroup.dayName] || 4;
                        const hasExtraRecords = dayGroup.classes.length > maxSlots;
                        return hasExtraRecords ? (
                          <Alert 
                            severity="warning" 
                            sx={{ 
                              m: 2, 
                              borderRadius: 2,
                              '& .MuiAlert-message': {
                                width: '100%'
                              }
                            }}
                          >
                            ⚠️ This day has {dayGroup.classes.length} attendance record(s), but only {maxSlots} slot(s) are allowed for {dayGroup.dayOfWeek}. 
                            Please use the <strong>Modify</strong> button above to fix this by deleting or moving extra records.
                          </Alert>
                        ) : null;
                      })()}
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#fafafa' }}>
                              <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Course</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Instructor</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Topic</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Comments</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {dayGroup.classes.map((classItem, idx) => (
                              <TableRow 
                                key={idx}
                                sx={{ 
                                  '&:hover': { bgcolor: '#f5f5f5' },
                                  '&:last-child td': { border: 0 }
                                }}
                              >
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {classItem.startTime} - {classItem.endTime}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={classItem.courseName} 
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>{classItem.instructor}</TableCell>
                                <TableCell align="center">
                                  <Typography
                                    className={isUrduText(classItem.topic || '') ? 'urdu' : ''}
                                    variant="body2"
                                    sx={{
                                      ...getUrduSxProps(classItem.topic || ''),
                                      direction: 'ltr',
                                      textAlign: 'left'
                                    }}
                                  >
                                    {classItem.topic || '-'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography 
                                    className={isUrduText(classItem.comments || '') ? 'urdu' : ''} 
                                    variant="body2"
                                    sx={{
                                      ...getUrduSxProps(classItem.comments || ''),
                                      direction: 'ltr',
                                      textAlign: 'left'
                                    }}
                                  >
                                    {classItem.comments || '-'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                ))
              )}
            </Box>
          ) : (
            <Paper sx={{ p: 4, mt: 3, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                Please select a week to view the report
              </Typography>
            </Paper>
          )}
        </Box>
        )}

        {/* Total Classes Report Tab */}
        {activeTab === 2 && (
        <Box mt={4}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.5rem' }}>
              Total Classes wise Report
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={handleExportTotalClassesReport}
              startIcon={<DownloadIcon />}
              sx={{
                borderColor: '#4CAF50',
                color: '#4CAF50',
                '&:hover': { borderColor: '#45a049', bgcolor: '#e8f5e9' },
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              Download Report
            </Button>
          </Box>
          
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <Card 
                elevation={3}
                sx={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  borderRadius: 3,
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 12px 20px rgba(102, 126, 234, 0.4)'
                  }
                }}
              >
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 500, opacity: 0.9 }}>
                    📚 Total Classes
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {summary.totalClasses}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                    Across all courses
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card 
                elevation={3}
                sx={{ 
                  background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  color: 'white',
                  borderRadius: 3,
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 12px 20px rgba(17, 153, 142, 0.4)'
                  }
                }}
              >
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 500, opacity: 0.9 }}>
                    ✅ Classes Conducted
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {summary.totalConducted}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                    {summary.totalClasses > 0 
                      ? `${((summary.totalConducted / summary.totalClasses) * 100).toFixed(1)}% completed`
                      : '0% completed'
                    }
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card 
                elevation={3}
                sx={{ 
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: 'white',
                  borderRadius: 3,
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 12px 20px rgba(245, 87, 108, 0.4)'
                  }
                }}
              >
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 500, opacity: 0.9 }}>
                    ⏳ Remaining Classes
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700 }}>
                    {summary.totalRemaining}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                    {summary.totalClasses > 0 
                      ? `${((summary.totalRemaining / summary.totalClasses) * 100).toFixed(1)}% pending`
                      : '0% pending'
                    }
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <DataGrid
              rows={courseTableData}
              getRowId={row => row.courseName}
              columns={[
                { field: 'courseName', headerName: 'Course Name', width: 200 },
                { field: 'totalClasses', headerName: 'Total Classes', width: 150 },
                { field: 'totalClassesConducted', headerName: 'Total Classes Conducted', width: 200 },
                { field: 'remainingClasses', headerName: 'Remaining Classes', width: 200 },
                { 
                  field: 'lastTopic', 
                  headerName: 'Last Topic', 
                  width: 300,
                  renderCell: (params) => {
                    const isUrdu = isUrduText(params.value || '');
                    return (
                      <Typography
                        className={isUrdu ? 'urdu' : ''}
                        variant="body2"
                        sx={{
                          ...getUrduSxProps(params.value || ''),
                          direction: 'ltr',
                          textAlign: 'left',
                          whiteSpace: 'normal',
                          wordWrap: 'break-word',
                          py: 1
                        }}
                      >
                        {params.value || 'N/A'}
                      </Typography>
                    );
                  }
                },
                { field: 'instructor', headerName: 'Teacher', width: 200 }
              ]}
              slots={{ toolbar: GridToolbar }}
              pageSize={10}
              disableSelectionOnClick
              sx={{
                '& .MuiDataGrid-cell': {
                  alignItems: 'flex-start',
                  paddingTop: '8px',
                  paddingBottom: '8px'
                }
              }}
          />
        </Box>
        )}

        {/* Course-Wise Attendance Report Tab */}
        {activeTab === 3 && (
        <Box mt={4}>
          <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#2E7D32' }}>
                📚 Course-Wise Attendance Report
              </Typography>
              {selectedCourseForReport && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleExportCourseWiseAttendance}
                  startIcon={<DownloadIcon />}
                  sx={{
                    borderColor: '#4CAF50',
                    color: '#4CAF50',
                    '&:hover': { borderColor: '#45a049', bgcolor: '#e8f5e9' },
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  Download Report
                </Button>
              )}
            </Box>
            
            <FormControl fullWidth sx={{ mb: 3, maxWidth: 400 }}>
              <InputLabel>Select Course</InputLabel>
              <Select
                value={selectedCourseForReport}
                onChange={(e) => setSelectedCourseForReport(e.target.value)}
                label="Select Course"
              >
                <MenuItem value="">All Courses</MenuItem>
                {contextCourses
                  .filter(course => !selectedCampus || (course.campusCodes && course.campusCodes.includes(selectedCampus)))
                  .map((course) => (
                    <MenuItem key={course.name} value={course.name}>
                      {course.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            {selectedCourseForReport ? (
              (() => {
                // Filter attendance by selected course using context data (efficient - no DB calls)
                const filteredAttendance = contextAttendance.filter(att => {
                  const matchesCourse = att.courseName === selectedCourseForReport;
                  const matchesCampus = att.campusCode === selectedCampus;
                  return matchesCourse && matchesCampus;
                });

                // Process attendance records
                const attendanceRecords = filteredAttendance.map(att => {
                  const start = dayjs(att.startTime?.replace(/-(\d{1,2}):(\d{2})$/, ' $1:$2'), 'YYYY-MM-DD HH:mm');
                  const { presentCount, absentCount, leaveCount } = dedupeAttendanceBuckets(att);
                  const totalStudents = presentCount + absentCount + leaveCount;
                  const pct = sessionPresentPct(presentCount + leaveCount, campusActiveStudentCount);

                  return {
                    id: att.id,
                    date: start.isValid() ? start.format('DD/MM/YYYY') : 'N/A',
                    time: start.isValid() ? start.format('hh:mm A') : 'N/A',
                    dateTime: start.isValid() ? start.format('DD/MM/YYYY - hh:mm A') : att.startTime || 'N/A',
                    dateValue: start.isValid() ? start.valueOf() : 0,
                    present: presentCount,
                    absent: absentCount,
                    leaves: leaveCount,
                    total: totalStudents,
                    attendancePct: pct === null || isNaN(pct) ? null : pct,
                    topic: att.topic || '-',
                    comments: att.comments || '-',
                    instructor: getInstructorNameForCourseName(att.courseName, contextCourses, instructorById)
                  };
                }).sort((a, b) => b.dateValue - a.dateValue); // Sort by date (newest first)

                return (
                  <>
                    {/* Attendance Records Table */}
                    {attendanceRecords.length > 0 ? (
                      <TableContainer component={Paper} variant="outlined">
                        <Table>
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                              <TableCell sx={{ fontWeight: 600 }}>Date & Time</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 600 }}>Present</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 600 }}>Absent</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 600 }}>Leaves</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 600 }}>Total</TableCell>
                              <TableCell align="center" sx={{ fontWeight: 600 }}>Attendance %</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Topic</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Comments</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {attendanceRecords.map((record) => (
                              <TableRow 
                                key={record.id}
                                sx={{ 
                                  '&:hover': { bgcolor: '#f9f9f9' },
                                  '&:nth-of-type(even)': { bgcolor: '#fafafa' }
                                }}
                              >
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {record.dateTime}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Chip 
                                    label={record.present} 
                                    size="small" 
                                    color="success"
                                    sx={{ fontWeight: 600 }}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Chip 
                                    label={record.absent} 
                                    size="small" 
                                    color="error"
                                    sx={{ fontWeight: 600 }}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Chip 
                                    label={record.leaves} 
                                    size="small" 
                                    color="warning"
                                    sx={{ fontWeight: 600 }}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {record.total}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {record.attendancePct === null || record.attendancePct === undefined
                                      ? '—'
                                      : `${record.attendancePct.toFixed(1)}%`}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography 
                                    className={isUrduText(record.topic || '') ? 'urdu' : ''} 
                                    variant="body2"
                                    sx={{
                                      ...getUrduSxProps(record.topic || ''),
                                      direction: 'ltr',
                                      textAlign: 'left',
                                      maxWidth: 300
                                    }}
                                  >
                                    {record.topic}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography 
                                    className={isUrduText(record.comments || '') ? 'urdu' : ''} 
                                    variant="body2"
                                    sx={{
                                      ...getUrduSxProps(record.comments || ''),
                                      direction: 'ltr',
                                      textAlign: 'left',
                                      maxWidth: 200
                                    }}
                                  >
                                    {record.comments}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    startIcon={<EditIcon />}
                                    onClick={() => {
                                      // record.date is in DD/MM/YYYY format, pass as-is (CourseSelector now handles this format)
                                      navigate(`/attendance?date=${record.date}`);
                                    }}
                                    disabled={isPresident()}
                                    sx={{
                                      textTransform: 'none',
                                      fontWeight: 500
                                    }}
                                  >
                                    Modify
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Paper sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6" color="text.secondary">
                          No attendance records found for this course
                        </Typography>
                      </Paper>
                    )}
                  </>
                );
              })()
            ) : (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  Please select a course to view attendance records
                </Typography>
              </Paper>
            )}
          </Paper>
        </Box>
        )}

      </>
  );
};

export default ReportsList;
